import { BlockError } from '@/blocks.04-project/block-error/block-error';
import ProductControl from '@/blocks.04-project/product-control/product-control';
import { PaginationEvents } from '@/blocks.04-project/slider-pagination/slider-pagination';
import forEach from 'lodash-es/forEach';
import get from 'lodash-es/get';
import keyBy from 'lodash-es/keyBy';
import isEmpty from 'lodash-es/isEmpty';
import map from 'lodash-es/map';
import debounce from 'lodash-es/debounce';
import toArray from 'lodash-es/toArray';
import filter from 'lodash-es/filter';
import includes from 'lodash-es/includes';
import flatMap from 'lodash-es/flatMap';
import toNumber from 'lodash-es/toNumber';
import isNull from 'lodash-es/isNull';
import find from 'lodash-es/find';
import counter from '@/blocks.04-project/counter/counter.tmpl-specs/base.module';
import giftPopper from '@/blocks.04-project/gift-popper/gift-popper.tmpl-specs/base.module';
import merge from 'lodash-es/merge';

/**
 * Класс детальной карточки товара
 */
class ProductDetail extends ProductControl {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с классом product-detail
	 * @param options {Object} данные карточки
   */
  constructor({ elem, options }) {
    const opt = merge(options, {
      actionList: get(options, 'actionList', ['favorite', 'compare', 'share'])
    });
		super({ blockClass: 'product-detail', options: opt, elem });
		this.BlockError = new BlockError({ blockName: 'ProductDetail', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.ProductDetail = this;

    this.init();
  }

  async init() {
    this.availabilityModal = document.querySelector('#AVAILABILITY_MODAL');
    if (!this.availabilityModal) return this.BlockError.error('Отсутствует #AVAILABILITY_MODAL');

    await super.init();

    this.scrollMenu = this.block.querySelector('.product-detail__scroll-menu');
    if (!this.scrollMenu) return this.BlockError.error('Отсутствует .product-detail__scroll-menu');

    this.buy = this.block.querySelector('.product-detail__buy');
    if (!this.buy) return this.BlockError.error('Отсутствует .product-detail__buy');

    this.buyBottom = this.block.querySelector('.product-detail__buy-bottom');
    if (!this.buyBottom) return this.BlockError.error('Отсутствует .product-detail__buy-bottom');

    this.paginationContainer = this.block.querySelector('.product-detail__gallery .swiper-pagination');
    if (!this.paginationContainer) return this.BlockError.error('Отсутствует .product-detail__pagination .swiper-pagination');

    this.instantModal = document.querySelector('#INSTANT-BUY_MODAL');
    if (!this.instantModal) return this.BlockError.error('Отсутствует #INSTANT-BUY_MODAL');

    this.reserveModal = document.querySelector('#RESERVE_MODAL');

    this.initScrollMenu();
    this.updateScrollMenuOffsets();
    this.updateBuyBottomOffsets();
    await this.initImageSlider();
    this.instantModal.addEventListener('show.bs.modal', this.showInstantModalHandler);
		if (this.reserveModal) {
			this.reserveModal.addEventListener('show.bs.modal', this.showReserveModalHandler);
			this.availabilityModal.addEventListener('reserve.iv.availabilityMap', this.reserveAvailabilityHandler);
		}
    this.availabilityModal.addEventListener('shown.bs.modal', this.showAvailabilityModalModalHandler);

    this.block.classList.add('product-detail_inited');
  }

  /** Инициализация верхнего меню */
  initScrollMenu = () => {
    this.scrollMenuObserver = new IntersectionObserver(this.scrollMenuObserverHandler, {
      rootMargin: '-10px 0px 0px 0px',
      threshold: 0,
    });
    this.scrollMenuObserver.observe(this.buy);

    window.addEventListener('resize', this.debounceUpdateScrollMenuOffsets, { passive: true });
  };

  /** Инициализация слайдера */
  initImageSlider = async () => {
    if (this.swiper) this.swiper.destroy();
    else {
      const { Swiper, Modules } = await Promise.all([
        import(/* webpackChunkName: "Swiper" */ 'swiper'),
        import(
          /* webpackChunkName: "SwiperModules" */
          /* webpackExports: ['Pagination'] */
          'swiper/modules')
      ]).then((data) => {
        return {
          Swiper: data[0]?.Swiper,
          Modules: toArray(data[1]),
        };
      }).catch((err) => {
        throw new Error(err);
      });
      this.Swiper = Swiper;
      this.Modules = Modules;
    }

    this.swiper = new this.Swiper(this.block.querySelector('.product-detail__gallery .swiper'), {
      slidesPerView: 1,
      spaceBetween: 8,
      pagination: {
        el: this.paginationContainer,
        type: 'bullets',
        clickable: true,
      },
      on: {
        ...PaginationEvents,
        resize: (swiper) => {
          this.debounceToggleImageSlider();
          PaginationEvents.resize(swiper);
        },
      },
      breakpoints: {
        992: {
          allowTouchMove: false,
        }
      },
      modules: this.Modules,
    });
  };

  /**
   * Слежение за меню товара. Если оно скрылось, шапка отображается
   * @param entries{IntersectionObserverEntry[]}
   */
  scrollMenuObserverHandler = (entries) => {
    entries.forEach((entry) => {
        this.toggleScrollMenu(!entry.isIntersecting && window.scrollY > this.buy.offsetTop);
    });
  };

  /**
   * Переключение отображения верхней шапки
   * @param toggle{Boolean} - отобразить/скрыть
   */
  toggleScrollMenu = (toggle) => {
    this.block.classList.toggle('product-detail_menu-fixed', toggle);
  };

  debounceUpdateScrollMenuOffsets = debounce(() => {
    this.updateScrollMenuOffsets();
    this.updateBuyBottomOffsets();
  }, 300);

  /** Пересчёт размера нижней панели */
  updateBuyBottomOffsets = () => {
    document.body.style.setProperty('--detail-buy-panel-offset', window.innerWidth < 991 ? this.buyBottom.offsetHeight + 'px' : '0px');
  };

  /** Пересчёт размера фиксированной верхней шапки */
  updateScrollMenuOffsets = () => {
    this.scrollMenuHeight = this.scrollMenu.clientHeight;
    this.block.style.setProperty('--product-detail-scroll-menu-height', this.scrollMenuHeight + 'px');
  };

  debounceToggleImageSlider = debounce(() => {
    this.toggleImageSlider();
  }, 300);

  /** Скролл слайдера при изменении размера экрана на пк */
  toggleImageSlider = () => {
    //  Если ширина экрана больше 992, то листаем к первому слайду (из-за transform происходит смещение и не видно картинки)
    if (window.screen.width >= 992) {
      this.swiper.slideTo(0);
    }
  };

  showInstantModalHandler = () => {
    this.renderInstantModal();
  };

  showReserveModalHandler = () => {
    this.renderReserveModal();
  };

  showAvailabilityModalModalHandler = () => {
    this.updateAvailabilityMap();
  };

  reserveAvailabilityHandler = () => {
    window.bootstrap.Modal.getOrCreateInstance(this.reserveModal)?.show();
  };

  async updateAvailabilityMap() {
    const offerId = get(this.currentOffer, 'id');
		const availabilityCount = get(this.currentOffer, 'availabilityCount', 0);
		const availability = !!availabilityCount && !isEmpty(this.getReserveResellers());

    if (!this.availabilityMap) {
      const AvailabilityMap = window.Block.AvailabilityMap ? window.Block.AvailabilityMap : await window.ImportBlock.AvailabilityMap();
      const elem = this.availabilityModal.querySelector('.availability-map:not(.availability-map_inited)');
      if (!elem) return this.BlockError.error('Отсутствует .availability-map');

			this.availabilityMap = new AvailabilityMap(elem, {
				resellers: get(this.currentOffer, 'availability', []),
				availability: availability
			});
      this.mapId = offerId;

    } else if (this.mapId !== offerId) {
			this.availabilityMap.updateData({
				resellers: get(this.currentOffer, 'availability', []),
				availability : availability
			});
      this.mapId = offerId;
    }
  }

  /***
   * Метод для программистов. Возвращает всех реселлеров в текущем оффере. которые подходят для резервирования
   * @return {Array} - Массив reseller с reserve: true
   */
  getReserveResellers = () => {
    return flatMap(get(this.currentOffer, 'availability', []), ({ resellers }) => filter(resellers, { 'reserve': true }));
  };

  render() {
    super.render();
    if (this.offerHash !== this.getCurrentHash()) {
      this.renderShortSpecs();
      this.renderSpecs();
      this.renderImages();
    }

    this.renderSku();
    this.renderPromoBadges();
    this.renderReviews();
    this.renderInstant();
    this.renderScrollMenuImg();
    this.renderReserve();
  }

  renderInstantModal() {
    const container = this.instantModal.querySelector('[data-instant-buy-modal]');
    if (!container) return this.BlockError.error('Отсутствует [data-instant-buy-modal] в #INSTANT-BUY_MODAL');

    const article = get(this.currentOffer, 'article');
    const images = get(this.currentOffer, 'photos', []);
    const { src, alt, title } = images[0];
    const name = get(this.currentOffer, 'name');
    const price = get(this.currentOffer, 'price');
    const oldPrice = get(this.currentOffer, 'oldPrice');
    const sku = this.getSku();
    const { color } = keyBy(sku, 'key');
    const colorCurrentText = get(color, 'current.text', '');

    container.innerHTML = '';
    container.insertAdjacentHTML('beforeend', this.bh.apply([
      {block: 'product-detail', elem: 'modal-card', content: [
          {block: 'product-detail', elem: 'modal-img', content: [
              {block: 'img', mods: {lazy: true}, src, attrs: {alt, title}},
            ]},
          {content: [
              {cls: 'h6 mb-4', content: name},
              {block: 'product-detail', elem: 'modal-specs', content: [
                  {tag: 'span', content: 'Арт. ' + article},
                  colorCurrentText && {tag: 'span', content: [
                      {tag: 'span', content: window.BX?.message('productDetailColorTitle') || 'Цвет' + ': '},
                      {tag: 'span', content: colorCurrentText},
                    ]},
                ]},
              {block: 'product-detail', elem: 'modal-price', content: [
                  {tag: 'span', content: price},
                  oldPrice && {tag: 'del', content: oldPrice},
                ]}
            ]}
        ]},
    ]));
  }

  renderInstant() {
    const containers = this.getBlockElements('[data-instant-buy]');
    const article = get(this.currentOffer, 'article');

    forEach(containers, (container) => {
      container.innerHTML = '';
      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {block: 'btn', cls: `btn-secondary text-uppercase fw-medium ${!this.canBuy || !article ? 'disabled' : ''}`, tag: 'a',
          attrs: {'data-bs-toggle': 'modal', 'data-bs-target': '#INSTANT-BUY_MODAL'},
          content: window.BX?.message('productDetailInstantBuy') || 'Купить сейчас'},
      ]));
    });
  }

  renderReserveModal = () => {
    const container = this.reserveModal.querySelector('[data-reserve-modal]');
    if (!container) return this.BlockError.error('Отсутствует [data-reserve-modal] в #RESEКVE_MODAL');

    const article = get(this.currentOffer, 'article');
    const images = get(this.currentOffer, 'photos', []);
    const { src, alt, title } = images[0];
    const name = get(this.currentOffer, 'name');
    const price = get(this.currentOffer, 'price');
    const oldPrice = get(this.currentOffer, 'oldPrice');
    const sku = this.getSku();
    const { color } = keyBy(sku, 'key');
    const colorCurrentText = get(color, 'current.text', '');

    container.innerHTML = '';
    container.insertAdjacentHTML('beforeend', this.bh.apply([
      {block: 'product-detail', elem: 'modal-card', content: [
          {block: 'product-detail', elem: 'modal-img', content: [
              {block: 'img', mods: {lazy: true}, src, attrs: {alt, title}},
            ]},
          {content: [
              {cls: 'h6 mb-4', content: name},
              {block: 'product-detail', elem: 'modal-specs', content: [
                  {tag: 'span', content: 'Арт. ' + article},
                  colorCurrentText && {tag: 'span', content: [
                      {tag: 'span', content: window.BX?.message('productDetailColorTitle') || 'Цвет' + ': '},
                      {tag: 'span', content: colorCurrentText},
                    ]},
                ]},
              {block: 'product-detail', elem: 'modal-price', content: [
                  {tag: 'span', content: price},
                  oldPrice && {tag: 'del', content: oldPrice},
                ]}
            ]}
        ]},
    ]));
  };

  renderReserve() {
    const containers = this.getBlockElements('[data-reserve]');
    const article = get(this.currentOffer, 'article');
    const availabilityCount = get(this.currentOffer, 'availabilityCount', 0);
    const modalTitle = this.availabilityModal.querySelector('[data-availability-title]');
    const statusText = get(this.currentOffer, 'status.text');

    if (modalTitle) modalTitle.innerHTML = `${window.BX?.message('productDetailAvailabilityText') || 'В наличии'} ${(window.BX?.message('productDetailAvailabilityCount') || 'в #COUNT# магазинах').replace('#COUNT#', `${availabilityCount}`)}`;

    forEach(containers, (container) => {
      container.innerHTML = '';

      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {block: 'product-detail', elem: 'availability', content: [
            availabilityCount ?
              [
                {tag: 'span', content: window.BX?.message('productDetailAvailabilityText') || 'В наличии '},
                {block: 'a', mods: {underline: true}, tag: 'button', attrs: {'data-bs-toggle': 'modal', 'data-bs-target': '#AVAILABILITY_MODAL'},
                  content: (window.BX?.Loc?.getMessagePlural('product-availability-count', availabilityCount, {'#COUNT#': availabilityCount}) || 'в #COUNT# магазинах').replace('#COUNT#', `${availabilityCount}`) }
              ] :
              [
                {tag: 'span', cls: 'fs-24 fw-medium', content: statusText}
              ]
          ]},
        !!availabilityCount && !isEmpty(this.getReserveResellers()) && {block: 'btn', cls: `btn-secondary py-8 ${!this.canBuy || !article ? 'disabled' : ''}`, attrs: {'data-bs-toggle': 'modal', 'data-bs-target': '#RESERVE_MODAL'}, content: window.BX?.message('productDetailReserve') || 'Зарезервировать'}
      ]));
    });
  }

  renderReviews({ langVar = 'productDetailReviews', customText = '#COUNT# Отзывов', icon = false } = {}) {
    super.renderReviews({langVar, customText, icon });
  }

  renderSku() {
    const sku = this.getSku();
    const { color } = keyBy(sku, 'key');
    const colorValues = get(color, 'values', []);
    const colorCurrentId = get(color, 'current.id', '');
    const colorCurrentText = get(color, 'current.text', '');
    const containers = this.getBlockElements('[data-sku]');

    const isWhite = (hex) => {
      const rgb = hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
        ,(m, r, g, b) => '#' + r + r + g + g + b + b)
        .substring(1).match(/.{2}/g)
        .map(x => parseInt(x, 16));
      return (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) > 186;
    };

    forEach(containers, (container, idx) => {
      container.innerHTML = '';
      if (isEmpty(colorValues)) return;
      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {block: 'product-detail', elem: 'color-list', mods: {'first-render': this.offerHash === this.getCurrentHash()}, content: map(colorValues, ({ id, text, value }) => [
            {block: 'form-check', mods: {big: true}, mix:
                { block: 'product-detail', elem: 'color-radio',
                  mods: {
                    white: !isWhite(value),
                    promo: get(this.saleColors, id, false) },
                },
              attrs: {style: `--radio-color: ${value}`, title: text},
              props: {type: 'radio', name: `PRODUCT-DETAIL_COLOR_${idx}`, value: id, checked: colorCurrentId === id}},
          ])},
        {block: 'product-detail', elem: 'color-title', content: [
            {tag: 'span', content: window.BX?.message('productDetailColorTitle') || 'Цвет'},
            {content: colorCurrentText},
          ]},
      ]));
    });
  }

  renderScrollMenuImg() {
    const images = get(this.currentOffer, 'photos', []);
    const containers = this.getBlockElements('[data-scroll-menu-img]');
    const { src, alt, title } = isEmpty(images) ? {src: './images/default-image/default-image.svg', alt: '', title: ''} : images[0];

    forEach(containers, (container) => {
      container.innerHTML = '';
      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {block: 'img', mods: {lazy: true}, src, attrs: {alt, title }},
      ]));
    });
  }

  renderShortSpecs() {
    const shortSpecs = get(this.currentOffer, 'shortSpecifications', null);
    const containers = this.getBlockElements('[data-short-specifications]');

    forEach(containers, (container) =>  {
      container.innerHTML = '';
      if (!shortSpecs) return;

      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {block: 'specification-table', tag: 'table', content: [
            {elem: 'body', tag: 'tbody', content: this.getSpecHtml(shortSpecs)},
          ]},
        ]));
      window.Block.Copy.init(container, true);
    });
  }

  renderSpecs() {
    const specs = get(this.currentOffer, 'specifications', null);
    const containers = this.getBlockElements('[data-specifications]');
    forEach(containers, (container) =>  {
      container.innerHTML = '';
      if (!specs) return;

      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {block: 'specification-table', tag: 'table', content: [
            {elem: 'body', tag: 'tbody', content: this.getSpecHtml(specs)},
          ]},
      ]));
      window.Block.Copy.init(container, true);
    });
  }

  renderBasket() {
    const id = get(this.currentOffer, 'id');
    const request = get(this.currentOffer, 'priceOnRequest', false);
    const substitutesUrl = get(this.currentOffer, 'substitutesUrl', '');
    const quantity = toNumber(get(this.currentOffer, 'quantity', null));
    const isExistBasket = !isNull(get(window, 'Block.Basket.products', null));
    const basket = toNumber(get(find(window.Block.Basket.products, { offerId: id }), 'quantity', 0));
    const isInBasket = basket > 0;

    const containers = this.getBlockElements('[data-basket]');
    if (!containers.length) return;

    forEach(containers, (container) => {
      container.innerHTML = '';
      container.insertAdjacentHTML('beforeend', this.bh.apply([
        !request && [
          {block: this.blockClass, elem: 'counter', content: [
              counter({
                attrs: {
                  'data-out-hide': true,
                  'data-coming-hide': true,
                  'data-control-counter': true,
                },
                value: basket,
                max: quantity,
                disabled: !this.canBuy || !quantity,
              }),
            ]},
          {block: 'btn', cls: 'btn-primary btn-lg', tag: 'button',
            attrs: {
              'data-out-hide': !!substitutesUrl,
              'data-control-cart': true,
              disabled: !this.canBuy || !isExistBasket
            },
            content: window.BX?.message('addInBasket') || 'В корзину'},
          substitutesUrl && {block: 'btn', cls: 'btn-primary-light fw-medium', attrs: {'data-out-visible': true, href: substitutesUrl}, content: window.BX?.message('findSame') || 'Выбрать похожие'},
        ],
        request && {block: 'btn', cls: 'btn-primary', tag: 'button', attrs: {'data-control-request-price': true}, content: window.BX?.message('priceOnRequest') || 'Цена по запросу'},
      ]));
    });

    this.toggleCounter({ toggle: this.canBuy && isInBasket, quantity: basket });
  }

  getSpecHtml(data) {
    return map(data, ({ name, values }, specIndex) => [
      {elem: 'row', tag: 'tr', content: [
          {elem: 'cell-spec', mods: {separator: isEmpty(values)}, tag: 'td', content: [
              {elem: 'line', tag: 'span', content: name},
            ]},
          !isEmpty(values) && {elem: 'cell-value', tag: 'td', content: map(values, ({ text, link, type }, idx) => [
              specIndex !== 0 ?
                [
                  {tag: link ? 'a' : 'span', cls: link && 'a_color_primary', attrs: link && {href: link }, content: [
                      type === 'boolean' ? [
                        {tag: 'span', cls: 'visual-hidden', content: '✓'},
                        {block: 'fi', cls: 'text-success', mods: {icon: 'check'}},
                      ] : [
                        text,
                        ++idx !== values.length && ', ',
                      ],
                    ]}
                ] :
                [
                  {tag: 'span', content: [
                      {tag: 'button', cls: 'd-inline-flex align-items-center',
                        attrs: {
                          'data-iv-copy': true,
                          'data-bs-title': 'Артикул скопирован',
                          'data-bs-placement': 'top',
                          type: 'button'
                        },
                        content: [
                          text,
                          {block: 'fi', mods: {icon: 'copy'}}
                        ]}
                    ]}
                ]
            ])},
        ]},
    ]);
  }

  renderPromoBadges() {
    const containers = this.getBlockElements('[data-badges-promo]');
    const badges = filter(get(this.currentOffer, 'badges'), ({ code }) => includes(['free_shipping', 'black_friday', 'cyber_monday', 'cyber_summer', 'guarantees', 'low_price', 'gift', 'sale_11_11', 'duhrer', 'schmieroll'], code));
    const gifts = get(this.currentOffer, 'gifts', []);
    const imageUrl = NOT_PROD ? './images/product-detail' : '/local/templates/ermenrich/dist/images/product-detail';
    const imageUrlBadges = NOT_PROD ? './images/badges' : '/local/templates/ermenrich/dist/images/badges';

		const badgesStyles = {
			'cyber_monday': 'badge-event badge-cyber-monday badge-blue-gradient text-uppercase',
      'cyber_summer': 'badge-event badge-cyber-summer text-uppercase',
			'black_friday': 'badge-event badge-black-friday text-uppercase me-12',
			'sale_11_11': 'badge-event badge-sale-eleven-eleven text-uppercase',
			'gift': 'badge-event badge-gift badge-indigo text-uppercase',
			'free_shipping': 'badge-event badge-free-delivery text-uppercase mb-8',
			'low_price': 'badge-event badge-price-guarantee badge-indigo text-uppercase me-12',
      'duhrer': 'badge-technology badge-duhrer',
      'schmieroll': 'badge-technology badge-schmieroll'
		};

    forEach(containers, (container) => {
      container.innerHTML = '';
      container.insertAdjacentHTML('beforeend', this.bh.apply(map(badges, ({ code, text, src, alt, title, link }) => [
        code === 'guarantees' ? {block: 'product-detail', elem: 'badge-guarantees', content: [
            {block: 'product-detail', elem: 'badge-warranty', content: [
                {block: 'img', mods: {lazy: true, contain: true}, src: `${imageUrl}/warranty.svg`, attrs: {alt: '5 лет, гарантийный срок', title: '5 лет, гарантийный срок'},  },
              ]},
            {block: 'product-detail', elem: 'badge-origin', content: [
                {block: 'img', mods: {lazy: true, contain: true}, src: `${imageUrl}/origin.svg`, attrs: {alt: 'Немецкое происхождение и подход', title: 'Немецкое происхождение и подход'},  },
              ]},
          ]} : code === 'duhrer' || code === 'schmieroll' ?
          {block: 'badge', cls: get(badgesStyles, code), content: [
              {block: 'img', mods: {lazy: true, contain: true}, src: `${imageUrlBadges}/${code}.svg`, attrs: {alt: alt, title: title}},
              link && {tag: 'noindex', content: [
                  {elem: 'link ', tag: 'a', attrs: {href: link, title: title}}
                ]},
            ]} : code === 'gift' ?
          {block: 'badge', tag: 'button', cls: get(badgesStyles, code), attrs: {
              'data-bs-toggle': 'tooltip',
              'data-bs-placement': 'bottom',
              'data-bs-fallback-placements': '[]',
              'data-bs-offset': '0, 8',
              'data-bs-custom-class': 'tooltip-gift',
              'data-bs-trigger': 'focus',
              'data-closest': true,
              'data-bs-container': '.product-control',
              'data-bs-html': 'true',
              'data-bs-title': `${this.bh.apply([giftPopper({ gifts, title: window.BX?.message('giftTooltipTitle') || 'Подарок' })])}`,
            }, content: text}
          : code in badgesStyles ?
          {block: 'badge', cls: get(badgesStyles, code), content: [
              {content: text},
              {block: 'img', mods: {lazy: true, contain: true}, src, attrs: {alt: title, title: title}},
              link && {tag: 'noindex', content: [
                  {elem: 'link ', tag: 'a', attrs: {href: link, title: title}}
                ]},
						]} :
					{block: 'product-detail', elem: 'badge', content: [
							{block: 'img', mods: {lazy: true, contain: true}, src, attrs: {alt: title, title: title}},
              link && {tag: 'noindex', content: [
                  {block: 'badge', elem: 'link ', tag: 'a', attrs: {href: link, title: title}}
                ]},
          ]}
      ])));
      container.addEventListener('shown.bs.tooltip', (event) => event.target.closest('[data-badges-promo]') && window.Block.CustomScrollbar.init(this.block));
      window.Block.Tooltip.init(container);
    });

  }

  renderImages() {
    const containers = this.getBlockElements('[data-images]');
    const hasVideo = get(this.currentOffer, 'hasVideo', false);
    let images = get(this.currentOffer, 'photos');
    if (isEmpty(images)) {
      images = [{
        src: './images/default-image/default-image.svg',
        description: '',
        preview: './images/default-image/default-image.svg',
        alt: '',
        title: ''
      }];
    }

    forEach(containers, (container) => {
      container.innerHTML = '';
      container.insertAdjacentHTML('beforeend', this.bh.apply([
        {cls: 'swiper-wrapper', attrs: {'data-iv-toggle': 'gallery'}, content: map(images, ({ src, description, preview, alt, title, width = 700, height = 400, videoCategory = null }) => [
            {cls: 'swiper-slide', attrs: {
								'data-gallery-target': true,
                'data-iframe': videoCategory ?  'true' : false,
								'href': src,
                'data-width': width,
                'data-height': height,
								'data-exthumbimage': 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
								'data-src-thumb': preview,
								'data-sub-html': description},
							content: [
                videoCategory ? [
                  {cls: 'w-100 ratio', attrs: {style: `--bs-aspect-ratio: ${height / width * 100}%`}, content: [
                      {cls: 'w-100', tag: 'iframe', attrs: { src, preload: 'metadata', allowfullscreen: true}}
                    ]},
                  ] : [
                  {block: 'product-detail', elem: 'image', content: [
                      {block: 'image', mods: {dynamic: true}, attrs: {width, height}, content: [
                          {block: 'img', mods: {contain: true}, src: preview, attrs: {alt, title, loading: 'lazy'},  },
                        ]},
                    ]},
                ],
                {block: 'product-detail', elem: 'show-all', content: [
                    {block: 'a', mods: {underline: true}, attrs: {href: '#', 'data-gallery-show-video': true},
                      content: [
                        hasVideo && (window.BX?.message('ShowAllPhotosAndVideos') || 'Смотреть все #COUNT# фото и видео').replace('#COUNT#', images.length),
                        !hasVideo && (window.BX?.message('ShowAllPhotos') || 'Смотреть все #COUNT# фото').replace('#COUNT#', images.length),
                      ]
                    },
                  ]},
              ]},
          ]),}
      ]));
      window.Block.Lightgallery.init(container, true);
    });
    this.initImageSlider();
  }

  destroy() {
    super.destroy();

    window.removeEventListener('resize', this.debounceUpdateScrollMenuOffsets, { passive: true });
    if (this.scrollMenuObserver) this.scrollMenuObserver.disconnect();
    if (this.instantModal) this.instantModal.removeEventListener('show.bs.modal', this.showInstantModalHandler);
    if (this.reserveModal) this.reserveModal.removeEventListener('show.bs.modal', this.showReserveModalHandler);
    if (this.availabilityModal) {
      this.availabilityModal.removeEventListener('shown.bs.modal', this.showAvailabilityModalModalHandler);
      this.availabilityModal.removeEventListener('reserve.iv.availabilityMap', this.reserveAvailabilityHandler);
    }
    if (this.block) {
      this.block.classList.remove('product-detail_inited');
      delete this.block.ProductDetail;
    }
  }

  static init() {
    Array.from(document.querySelectorAll('.product-detail:not(.product-detail_inited)')).forEach((elem) => new ProductDetail({ elem }));
  }
}

if (NOT_PROD) {
  const options = {
    data: {
      'id': 20181,
      'currentOfferId': 16331,
      'title': 'Микроскоп Levenhuk Rainbow 50L PLUS',
      'description': '',
      'vat': 'С учетом НДС',
      'colors': [
        {
          'id': '#cccccc',
          'value': '#cccccc',
          'text': 'Лунный камень'
        },
        {
          'id': '#9063cd',
          'value': '#9063cd',
          'text': 'Аметист'
        },
        {
          'id': '#0099cc',
          'value': '#0099cc',
          'text': 'Лазурь'
        },
        {
          'id': '#ddf642',
          'value': '#ddf642',
          'text': 'Лайм'
        },
        {
          'id': '#e03c31',
          'value': '#e03c31',
          'text': 'Апельсин'
        }
      ],
      'languages': [],
      'rating': 1.3,
      'reviews': 8,
      'offers': [
        {
          'id': 16319,
          'name': 'Микроскоп Levenhuk Rainbow 50L PLUS',
          'article': '69051',
          'url': '/catalogue/mikroskopy/microscope-levenhuk-rainbow-50l-plus-orange/?oid=16319',
          'substitutesUrl': '#',
          'hasVideo': true,
          'share': [
            {
              'url': '#',
              'icon': 'copy-link',
              'text': 'Скопировать ссылку',
              'target': 'copy',
            },
            {
              'url': '#',
              'icon': 'vk',
              'text': 'Вконтакте',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'tg',
              'text': 'Telegram',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'whatsapp',
              'text': 'WhatsApp',
              'target': '_blank',
            },
          ],
          'photos': [
            {
              'videoCategory': 'vk',
              'src': 'https://vk.com/video_ext.php?oid=-18118692&id=456239377',
              'preview': '/upload/iv_resize_cache/iblock/39f/wqexgvp2ij78jscs4vjmg1qnl3ei6ipn/69051_levenhuk-rainbow-50l-plus-moonstone_00_500_30008ea8398442c2016da2c9be909456b353.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'videoCategory': 'rutube',
              'src': 'https://rutube.ru/play/embed/6b43eb0a864ddd9fe1cdc5464a0208e1/',
              'preview': '/upload/iv_resize_cache/iblock/39f/wqexgvp2ij78jscs4vjmg1qnl3ei6ipn/69051_levenhuk-rainbow-50l-plus-moonstone_00_500_30008ea8398442c2016da2c9be909456b353.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'videoCategory': 'youtube',
              'width': '1920',
              'height': '1080',
              'src': 'https://www.youtube.com/embed/oIho2PsueHw',
              'preview': '/upload/iv_resize_cache/iblock/39f/wqexgvp2ij78jscs4vjmg1qnl3ei6ipn/69051_levenhuk-rainbow-50l-plus-moonstone_00_500_30008ea8398442c2016da2c9be909456b353.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'videoCategory': 'facebook',
              'width': '276',
              'height': '476',
              'src': 'https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Freel%2F342932244962548%2F&show_text=false&t=0',
              'preview': '/upload/iv_resize_cache/iblock/39f/wqexgvp2ij78jscs4vjmg1qnl3ei6ipn/69051_levenhuk-rainbow-50l-plus-moonstone_00_500_30008ea8398442c2016da2c9be909456b353.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21.jpg',
              'preview': '/upload/iv_resize_cache/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21_500_503148476883d4df5451f62283e6556d.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: яркие биологические микроскопы с увеличением до 1280х. В комплекте набор для экспериментов, линза Барлоу, кейс. В линейке есть цифровая модель с камерой 2 Мпикс (в цвете Moonstone)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 2'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo.jpg',
              'preview': '/upload/iv_resize_cache/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo_500_40c086c959799f77e649417cf61c22e8.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: красивые учебные микроскопы для дома и школы',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 3'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/06a/etvh90cvbajzueb0kqh5ntjsdplzm6m7.jpg',
              'preview': '/upload/iv_resize_cache/uf/06a/etvh90cvbajzueb0kqh5ntjsdplzm6m7_500_81001740fe0180619f94e34157f9020e.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: микроскоп и все аксессуары удобно и безопасно умещаются в кейсе',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 4'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/414/k3omqnr7o0gnjrqkss72pkwd5t7m2pul/69051_levenhuk-rainbow-50l-plus-moonstone_04_ru.jpg',
              'preview': '/upload/iv_resize_cache/uf/414/k3omqnr7o0gnjrqkss72pkwd5t7m2pul/69051_levenhuk-rainbow-50l-plus-moonstone_04_ru_500_723f4f79ffaf5b0b45d2d0729cb5a529.webp',
              'description': 'В комплекте с каждым микроскопом Levenhuk Rainbow набор для опытов: готовые микропрепараты и чистые предметные стекла, флаконы с образцами материалов, пинцет, микротом, инкубатор для артемии, пипетка, а также руководство «Интересный микроскоп. Изучаем микромир» ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 5'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/51c/4tq30pte8l6ewd7nfsthsz786szot2ef.jpg',
              'preview': '/upload/iv_resize_cache/uf/51c/4tq30pte8l6ewd7nfsthsz786szot2ef_500_01cce40d6c9ce6b29050a96bac41e695.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: монокулярный биологический микроскоп с увеличением от 64 до 1280 крат',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 6'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/035/39z07n9wxxb3eadf0m638ennduwhjq05.jpg',
              'preview': '/upload/iv_resize_cache/uf/035/39z07n9wxxb3eadf0m638ennduwhjq05_500_59a8967d616d9e8747ca8e659bbd13d9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: окулярная насадка микроскопа наклонена на угол 45° к вертикали, может вращаться на 360° и фиксироваться в любом положении',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 7'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f1f/2ecn18212hb1j0n96bfsqjz9ezrp8qqw.jpg',
              'preview': '/upload/iv_resize_cache/uf/f1f/2ecn18212hb1j0n96bfsqjz9ezrp8qqw_500_bb6ee670ef77cc8c2a9f8b593254c8c2.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: прочный металлический корпус идеален для частого использования в учебных заведениях или дома',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 8'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/50e/fytmmuaot3gu29r9sn14239s2uh6anax.jpg',
              'preview': '/upload/iv_resize_cache/uf/50e/fytmmuaot3gu29r9sn14239s2uh6anax_500_a1fc872bb942de62f760ee8b5a935f28.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: питание осветителей может производиться как от батареек, так и от сетевого адаптера',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 9'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c2c/jpc1zh6f1xz62wtml7e2o31te1t77t4g.jpg',
              'preview': '/upload/iv_resize_cache/uf/c2c/jpc1zh6f1xz62wtml7e2o31te1t77t4g_500_faf6adea622f3a31a18925bd828d98e9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: револьверное устройство на 3 объектива: 4x, 10x, 40xs (подпружиненный)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 10'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/1e4/jd2boy26mkjhdd4ri6w364g2ksevb28m.jpg',
              'preview': '/upload/iv_resize_cache/uf/1e4/jd2boy26mkjhdd4ri6w364g2ksevb28m_500_39554caad38a143b21a69d5332f35057.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: окуляр, установленный на микроскоп; качественная стеклянная оптика обеспечивает четкое изображение',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 11'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/373/p8bk2xui4mkx9cmi7e1yx900dgd4lfry.jpg',
              'preview': '/upload/iv_resize_cache/uf/373/p8bk2xui4mkx9cmi7e1yx900dgd4lfry_500_73fea9926469481ac486faf94a45a604.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: перед окуляром можно установить линзу Барлоу, которая увеличит изображение вдвое',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 12'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky.jpg',
              'preview': '/upload/iv_resize_cache/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky_500_53369f00105838a76808acba08c062c9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: линза Барлоу 2x в комплекте',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 13'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/472/k3yr5bwnjw91175ph942f3arvz1uq4yu.jpg',
              'preview': '/upload/iv_resize_cache/uf/472/k3yr5bwnjw91175ph942f3arvz1uq4yu_500_60f2a7cace9829c838d7c505d9ec8c63.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: верхняя и нижняя экономичные светодиодные подсветки с регулировкой яркости',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 14'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/5e1/olfwddrk32xv0bd3i9mql5l637dbfr0a.jpg',
              'preview': '/upload/iv_resize_cache/uf/5e1/olfwddrk32xv0bd3i9mql5l637dbfr0a_500_77b3fe7046647ae928be189582f013be.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: снабжен удобными ручками фокусировки для перемещения предметного столика с зажимами',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 15'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/5bb/veaas86h5uy8f1638dq5c4hsdptmjqyi.jpg',
              'preview': '/upload/iv_resize_cache/uf/5bb/veaas86h5uy8f1638dq5c4hsdptmjqyi_500_e4ef4320b50eea87625ac257deb10530.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: батарейный отсек',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 16'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh.jpg',
              'preview': '/upload/iv_resize_cache/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh_500_c6fbc40acb25434688a05de074ef59dd.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: сетевой адаптер',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 17'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9.jpg',
              'preview': '/upload/iv_resize_cache/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9_500_73d65e65c81edbecd0739e8244a01261.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: прочный пластиковый кейс',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 18'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx.jpg',
              'preview': '/upload/iv_resize_cache/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx_500_4848fa52bef0860df0e0812a29d6edfa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Moonstone: красивая подарочная коробка',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 19'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs.jpg',
              'preview': '/upload/iv_resize_cache/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs_500_987aef196b3dd5cafb328e85c68a51c1.webp',
              'description': 'Высушенный апельсин под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 20'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi.jpg',
              'preview': '/upload/iv_resize_cache/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi_500_82cfd171f0d8ba9fefe41b4875afcdbc.webp',
              'description': 'Пыль под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 21'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv.jpg',
              'preview': '/upload/iv_resize_cache/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv_500_70962e2df1877bb21d99d0c3cf61a590.webp',
              'description': 'Гитарные струны под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 22'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz.jpg',
              'preview': '/upload/iv_resize_cache/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz_500_cf83db71b24fd0d71c24e3d95bb6c54e.webp',
              'description': 'Можжевельник под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 23'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db.jpg',
              'preview': '/upload/iv_resize_cache/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db_500_89a413e0e9a594d6fe8ce966a9a717c8.webp',
              'description': 'Печать на ткани под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 24'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m.jpg',
              'preview': '/upload/iv_resize_cache/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m_500_8bf34baea4b43d14426c45aaf92cb024.webp',
              'description': 'Редис под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 25'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk.jpg',
              'preview': '/upload/iv_resize_cache/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk_500_ccc664529b3f604536505aa7c512b62b.webp',
              'description': 'Ножка паука под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 26'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v.jpg',
              'preview': '/upload/iv_resize_cache/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v_500_ae01fd454117998a5478475a5adf0952.webp',
              'description': 'Сера на спичке под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 27'
            }
          ],
          'margin': [],
          'currency': 'RUB',
          'priceValue': 15490,
          'price': '15 490 &#8381;',
          'oldPrice': '11490 &#8381',
          'quantity': 238,
          'color': '#cccccc',
          'lang': null,
          'status': {
            'code': 'IN_STOCK',
            'text': 'В наличии'
          },
          'badges': [
            {
              'icon': 'polygon',
              'code': 'guarantees',
              'text': '5 лет гарантийный срок'
            },
            {
              'icon': 'polygon',
              'code': 'duhrer',
              'link': 'https://www.google.com/',
              'title': 'Используется технология DűhrerSN',
            },
            {
              'icon': 'polygon',
              'code': 'schmieroll',
              'link': 'https://www.google.com/',
            },
            {
              'icon': 'polygon',
              'code': 'hot',
              'text': 'бестселлер'
            },
            {
              'icon': 'polygon',
              'code': 'free_shipping',
              'text': 'бесплатная<br>доставка',
              'src': './images/badges/free-delivery.svg',
							'title': 'Бесплатная доставка'
            },
            {
              'icon': 'polygon',
              'code': 'gift',
              'text': 'подарок'
            },
            {
              'src': './images/badges/11-11.svg',
              'code': 'sale_11_11',
              'text': 'Роняем<br>цены',
							'title': 'Роняем цены'
            },
            {
              'icon': 'infinity',
              'code': 'lifetime',
              'text': 'пожизненная гарантия'
            },
            {
              'icon': 'infinity',
              'code': 'new',
              'text': 'новинки'
            },
            {
              'icon': 'infinity',
              'code': 'hot',
              'text': 'топ продаж'
            },
            {
              'icon': 'infinity',
              'code': 'sale',
              'text': 'распродажа'
            },
            {
              'icon': 'infinity',
              'code': 'discount_percent',
              'text': '10%'
            },
          ],
          'shortSpecifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69051,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/info/brands/levenhuk/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            }
          ],
          'specifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69051,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/catalogue/mikroskopy/filter/override_reference_parameter_1022-is-levenhuk/apply/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип',
              'values': [
                {
                  'text': 'биологические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7492/apply/'
                },
                {
                  'text': 'световые/оптические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7499/apply/'
                },
              ]
            },
            {
              'name': 'Тип насадки',
              'values': [
                {
                  'text': 'монокулярная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1001-is-reference_parameteroption_7550/apply/'
                }
              ]
            },
            {
              'name': 'Материал оптики',
              'values': [
                {
                  'text': 'оптическое стекло',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Насадка',
              'values': [
                {
                  'text': 'поворотная на 360°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Угол наклона окулярной насадки',
              'values': [
                {
                  'text': '45°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Увеличение, крат',
              'values': [
                {
                  'text': '64 — 1280',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диаметр окулярной трубки, мм',
              'values': [
                {
                  'text': '23.2',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Окуляры',
              'values': [
                {
                  'text': 'WF16x',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Объективы',
              'values': [
                {
                  'text': 'ахроматические: 4x, 10x, 40xs (подпружиненный)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Револьверное устройство',
              'values': [
                {
                  'text': 'на 3 объектива',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Предметный столик, мм',
              'values': [
                {
                  'text': '90x90',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диапазон перемещения предметного столика, мм',
              'values': [
                {
                  'text': '0–11 по вертикали',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Характеристики предметного столика',
              'values': [
                {
                  'text': 'с зажимами',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Конденсор',
              'values': [
                {
                  'text': 'N.A. 0,65',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диафрагма',
              'values': [
                {
                  'text': 'диск с диафрагмами (6 отверстий)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Фокусировка',
              'values': [
                {
                  'text': 'грубая',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Корпус',
              'values': [
                {
                  'text': 'металл',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Подсветка',
              'values': [
                {
                  'text': 'светодиодная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_994-is-reference_parameteroption_7092/apply/'
                }
              ]
            },
            {
              'name': 'Регулировка яркости',
              'values': [
                {
                  'text': '',
                  'link': '',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Источник питания',
              'values': [
                {
                  'text': '2 батарейки типа AA (нет в комплекте), 220 В, 50 Гц',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Питание от батареек',
              'values': [
                {
                  'text': 'есть',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип лампы подсветки',
              'values': [
                {
                  'text': '3–3,2 В (нижний и верхний осветители)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень пользователя',
              'values': [
                {
                  'text': 'для начинающих',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень сложности сборки и настройки',
              'values': [
                {
                  'text': 'просто',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Назначение',
              'values': [
                {
                  'text': 'школьные/учебные',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1084-is-reference_parameteroption_9969/apply/'
                }
              ]
            },
            {
              'name': 'Расположение подсветки',
              'values': [
                {
                  'text': 'комбинированная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_875-is-reference_parameteroption_7392/apply/'
                }
              ]
            },
            {
              'name': 'Метод исследования',
              'values': [
                {
                  'text': 'светлое поле',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1000-is-reference_parameteroption_6888/apply/'
                }
              ]
            },
            {
              'name': 'Набор для опытов в комплекте',
              'values': [
                {
                  'text': '',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_876-from-1-to-1/apply/',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Линза Барлоу в комплекте',
              'values': [
                {
                  'text': '2х',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Чехол/кейс/сумка в комплекте',
              'values': [
                {
                  'text': 'чехол пылезащитный, кейс',
                  'link': ''
                }
              ]
            }
          ],
          'stocks': [
            {
              'name': 'Общий сток',
              'value': 'В наличии (20+)',
              'tooltip': '',
              'status': 'IN_STOCK'
            }
          ],
          'availabilityCount': 12,
          'availability': [
            {
              'country': 'Россия',
              'icon': 'https://loc.levenhuk.com/upload/uf/876/0un55i10jr1x3o4ylv1c0bk01vxearqm/ru.png',
              'resellers': [
                {
                  'coordinates': [
                    '55.77592495510984',
                    '37.68445894032853'
                  ],
                  'resellerId': '2351',
                  'reseller': 'Магазин-флагман «Четыре глаза» у метро Бауманская',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Бакунинская ул., д. 23–41',
                    'phone': ['+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.76037332343009',
                    '37.62545196978284'
                  ],
                  'resellerId': '2352',
                  'reseller': 'Магазин «Четыре глаза» на Лубянке',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Театральный проезд, д. 5/1, «ЦДМ на Лубянке», 1 этаж, «Улица сказок»',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.66355',
                    '37.605539'
                  ],
                  'resellerId': '2353',
                  'reseller': 'Магазин «Четыре глаза» на Нахимовском',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Нахимовский пр., д. 11, корп. 1',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.81174',
                    '37.6045'
                  ],
                  'resellerId': '2354',
                  'reseller': 'Магазин «Четыре глаза» у метро Бутырская',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Огородный проезд, д. 9, корп. 18',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.870251',
                    '37.469581'
                  ],
                  'resellerId': '2355',
                  'reseller': 'Магазин «Четыре глаза» у метро Беломорская',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Смольная ул., д. 63Б, ТЦ «Экстрим», 1 этаж, павильон А-11',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.7648016',
                    '37.5580848'
                  ],
                  'resellerId': '2356',
                  'reseller': 'Магазин «Четыре глаза» на Пресне',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': '<p>Звенигородское ш., д. 4, ТЦ &laquo;Электроника на Пресне&raquo;, 1 этаж, павильон А-41, красная линия</p>',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.66517080802077',
                    '37.481573398418334'
                  ],
                  'resellerId': '2358',
                  'reseller': 'Магазин «Четыре глаза» у метро Юго-Западная',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'ул. Покрышкина, д. 4, ТЦ «Звездочка», первый этаж',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.983331792202605',
                    '37.14213660240179'
                  ],
                  'resellerId': '2360',
                  'reseller': 'Магазин «Четыре глаза» в Зеленограде',
                  'city': 'Москва',
                  'url': 'www.4glaza-zel.ru',
                  'extra': {
                    'address': 'г. Зеленоград, п. Андреевка, ул. Жилинская, строение 1, ТЦ «ЭНЕРГИЯ», 2 этаж (напротив «Ашана»)',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '4glaza.zel@gmail.com',
                    'zip_code': '',
                    'time': 'с 10:00 до 20:00 ежедневно, без перерывов'
                  },
                  'reserve': false
                },
                {
                  'coordinates': [
                    '60.057879',
                    '30.33205'
                  ],
                  'resellerId': '2374',
                  'reseller': 'Магазин «Четыре глаза» на Шостаковича',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Шостаковича ул., д. 5, корп. 1, (вход с улицы)',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '59.83207666720598',
                    '30.38698684028511'
                  ],
                  'resellerId': '2375',
                  'reseller': 'Магазин «Четыре глаза» на Гашека',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Ярослава Гашека ул., д. 4, корп. 1',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '60.012253',
                    '30.398492'
                  ],
                  'resellerId': '2377',
                  'reseller': 'Магазин «Четыре глаза» на Академической',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Гражданский пр., д. 41, корп. 2, лит. Б, ТК «Академ-Парк», цокольный этаж, павильон G12,2(3)\n',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '60.0029139',
                    '30.2289583'
                  ],
                  'resellerId': '2378',
                  'reseller': 'Магазин «Четыре глаза» на Богатырском',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Богатырский пр., д. 42, лит. А, гипермаркет «О’кей», помещение 49\n',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '59.88164800000001',
                    '30.315893'
                  ],
                  'resellerId': '2380',
                  'reseller': 'Магазин «Четыре глаза» на Электросиле',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Московский пр., д. 137, лит. А, гипермаркет «О’кей», помещение 24',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                }
              ]
            }
          ],
          'sale': null,
          'sales': [],
          'gifts': [
            {
              'src': 'http://placehold.it/100x100?text=lazy',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
          ]
        },
        {
          'id': 16322,
          'name': 'Микроскоп Levenhuk Rainbow 50L PLUS',
          'article': '69052',
          'url': '/catalogue/mikroskopy/microscope-levenhuk-rainbow-50l-plus-orange/?oid=16322',
          'margin': [],
          'currency': 'RUB',
          'priceValue': 15490,
          'price': '15 490 &#8381;',
          'oldPrice': '190 &#8381',
          'quantity': 14,
          'color': '#9063cd',
          'lang': null,
          'share': [
            {
              'url': '#',
              'icon': 'copy-link',
              'text': 'Скопировать ссылку',
              'target': 'copy',
            },
            {
              'url': '#',
              'icon': 'vk',
              'text': 'Вконтакте',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'tg',
              'text': 'Telegram',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'whatsapp',
              'text': 'WhatsApp',
              'target': '_blank',
            },
          ],
          'status': {
            'code': 'IN_STOCK',
            'text': 'В наличии'
          },
          'badges': [
            {
              'icon': 'polygon',
              'code': 'guarantees',
              'text': '5 лет гарантийный срок'
            },
            {
              'icon': 'polygon',
              'code': 'duhrer',
              'link': 'https://www.google.com/',
              'title': 'Используется технология DűhrerSN',
            },
            {
              'icon': 'polygon',
              'code': 'schmieroll',
              'link': 'https://www.google.com/',
            },
            {
              'icon': 'polygon',
              'code': 'free_shipping',
              'src': './images/badges/free-delivery.svg',
              'text': 'бесплатная<br>доставка',
							'title': 'Беслпатная доставка'
            },
            {
              'icon': 'polygon',
              'code': 'cyber_monday',
              'src': './images/badges/cyber-monday.svg',
              'text': 'кибер<br>понедельник',
							'title': 'Кибер понедельник'
            },
						{
							'src': './images/badges/price-guarantee.svg',
							'code': 'low_price',
							'text': 'Лучшая<br>цена',
							'title': 'Лучшая цена'
						},
            {
              'icon': 'polygon',
              'code': 'black_friday',
              'text': 'черная<br>пятница',
              'src': './images/badges/fire.svg',
							'title': 'Чёрная пятница'
            },
            {
              'icon': 'polygon',
              'code': 'gift',
              'text': 'подарок'
            },
            {
              'src': './images/badges/11-11.svg',
              'code': 'sale_11_11',
              'text': 'Роняем<br>цены',
							'title': 'Роняем цены'
            },
            {
              'icon': 'infinity',
              'code': 'lifetime',
              'text': 'пожизненная гарантия'
            },
            {
              'icon': 'infinity',
              'code': 'new',
              'text': 'новинки'
            },
            {
              'icon': 'infinity',
              'code': 'hot',
              'text': 'топ продаж'
            },
            {
              'icon': 'infinity',
              'code': 'discount_percent',
              'text': '10%'
            },
          ],
          'photos': [
            {
              'src': '/upload/iblock/c28/zzugnlak7wyx6y10ihhezsln7o825a6r/69052_levenhuk-rainbow-50l-plus-amethyst_00.jpg',
              'preview': '/upload/iv_resize_cache/iblock/c28/zzugnlak7wyx6y10ihhezsln7o825a6r/69052_levenhuk-rainbow-50l-plus-amethyst_00_500_3000fb146835e15a31e4ee43aa4465d0db75.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21.jpg',
              'preview': '/upload/iv_resize_cache/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21_500_503148476883d4df5451f62283e6556d.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: яркие биологические микроскопы с увеличением до 1280х. В комплекте набор для экспериментов, линза Барлоу, кейс. В линейке есть цифровая модель с камерой 2 Мпикс (в цвете Moonstone)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 2'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo.jpg',
              'preview': '/upload/iv_resize_cache/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo_500_40c086c959799f77e649417cf61c22e8.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: красивые учебные микроскопы для дома и школы',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 3'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/2a6/8so3wt0416e350qkg32tjztmu6ym2u6b/69077_levenhuk-rainbow-50l-plus-amethyst_03.jpg',
              'preview': '/upload/iv_resize_cache/uf/2a6/8so3wt0416e350qkg32tjztmu6ym2u6b/69077_levenhuk-rainbow-50l-plus-amethyst_03_500_3d60c421c397f8b5431f8b9eaeea9435.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: микроскоп и все аксессуары удобно и безопасно умещаются в кейсе',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 4'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/461/m1owtf6zp07tk67g3ax1ern9w9x5toai/69052_levenhuk-rainbow-50l-plus-amethyst_04_ru.jpg',
              'preview': '/upload/iv_resize_cache/uf/461/m1owtf6zp07tk67g3ax1ern9w9x5toai/69052_levenhuk-rainbow-50l-plus-amethyst_04_ru_500_b32c52500fb5599258715e6ed91d8ee4.webp',
              'description': 'В комплекте с каждым микроскопом Levenhuk Rainbow набор для опытов: готовые микропрепараты и чистые предметные стекла, флаконы с образцами материалов, пинцет, микротом, инкубатор для артемии, пипетка, а также руководство «Интересный микроскоп. Изучаем микромир» ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 5'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/390/9bccoxh7k296a0e0b64fofrg4fok648d/69077_levenhuk-rainbow-50l-plus-amethyst_05.jpg',
              'preview': '/upload/iv_resize_cache/uf/390/9bccoxh7k296a0e0b64fofrg4fok648d/69077_levenhuk-rainbow-50l-plus-amethyst_05_500_543d4b0738e3f478f956e48d76fd4e77.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: монокулярный биологический микроскоп с увеличением от 64 до 1280 крат',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 6'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/911/5a3kh4ly5al3s7eblpq9ao8h5bzhfcji/69077_levenhuk-rainbow-50l-plus-amethyst_06.jpg',
              'preview': '/upload/iv_resize_cache/uf/911/5a3kh4ly5al3s7eblpq9ao8h5bzhfcji/69077_levenhuk-rainbow-50l-plus-amethyst_06_500_b80d35e987ac3967e1f21a3f1e15209f.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: окулярная насадка микроскопа наклонена на угол 45° к вертикали, может вращаться на 360° и фиксироваться в любом положении',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 7'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/24c/hi7xkj0b5m40cfuy4p0ybo5d3u3ux7p6/69077_levenhuk-rainbow-50l-plus-amethyst_07.jpg',
              'preview': '/upload/iv_resize_cache/uf/24c/hi7xkj0b5m40cfuy4p0ybo5d3u3ux7p6/69077_levenhuk-rainbow-50l-plus-amethyst_07_500_06502e025efae5dbff92fe631ff2b8da.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: прочный металлический корпус идеален для частого использования в учебных заведениях или дома',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 8'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/868/o3m7w2k3o84tj7s98h4jx9u207hyix21/69077_levenhuk-rainbow-50l-plus-amethyst_08.jpg',
              'preview': '/upload/iv_resize_cache/uf/868/o3m7w2k3o84tj7s98h4jx9u207hyix21/69077_levenhuk-rainbow-50l-plus-amethyst_08_500_86d0692618c453b28178934faebb8433.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: питание осветителей может производиться как от батареек, так и от сетевого адаптера',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 9'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/18c/opjedy5muqivis8ni15m0ft2pz7sui0u/69072_levenhuk-rainbow-50l-amethyst_09.jpg',
              'preview': '/upload/iv_resize_cache/uf/18c/opjedy5muqivis8ni15m0ft2pz7sui0u/69072_levenhuk-rainbow-50l-amethyst_09_500_59db32b9fd5dec37be4d6b9342ac3679.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: револьверное устройство на 3 объектива: 4x, 10x, 40xs (подпружиненный)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 10'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/949/outrsvupu58v0rh18wlx97oxb49btnri/69077_levenhuk-rainbow-50l-plus-amethyst_10.jpg',
              'preview': '/upload/iv_resize_cache/uf/949/outrsvupu58v0rh18wlx97oxb49btnri/69077_levenhuk-rainbow-50l-plus-amethyst_10_500_f2cfacbc3c3f1824c26669b3ed336391.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: окуляр, установленный на микроскоп; качественная стеклянная оптика обеспечивает четкое изображение',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 11'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/570/dif6es3ukpl5quwze9kuyxrdcpttdw0a/69077_levenhuk-rainbow-50l-plus-amethyst_11.jpg',
              'preview': '/upload/iv_resize_cache/uf/570/dif6es3ukpl5quwze9kuyxrdcpttdw0a/69077_levenhuk-rainbow-50l-plus-amethyst_11_500_4ec7da847071dd7c29a84db7cd9d0353.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: перед окуляром можно установить линзу Барлоу, которая увеличит изображение вдвое',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 12'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky.jpg',
              'preview': '/upload/iv_resize_cache/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky_500_53369f00105838a76808acba08c062c9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: линза Барлоу 2x в комплекте',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 13'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/dd5/kgoxfjwbbgr89holn5cdbn5cq2vt9rak/74892_levenhuk-rainbow-50l-amethyst_13.jpg',
              'preview': '/upload/iv_resize_cache/uf/dd5/kgoxfjwbbgr89holn5cdbn5cq2vt9rak/74892_levenhuk-rainbow-50l-amethyst_13_500_2ca834e6f681eee9e8b85eacfadb9795.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: верхняя и нижняя экономичные светодиодные подсветки с регулировкой яркости',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 14'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4c3/msueg8bhksnwpcbjq48oih8yszztklmg/74892_levenhuk-rainbow-50l-amethyst_14.jpg',
              'preview': '/upload/iv_resize_cache/uf/4c3/msueg8bhksnwpcbjq48oih8yszztklmg/74892_levenhuk-rainbow-50l-amethyst_14_500_d0218f500e9d12534f8e12f94542374d.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: снабжен удобными ручками фокусировки для перемещения предметного столика с зажимами',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 15'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/8ba/mu9zmms61edv76nnygdlpeusl69znynx/74892_levenhuk-rainbow-50l-amethyst_15.jpg',
              'preview': '/upload/iv_resize_cache/uf/8ba/mu9zmms61edv76nnygdlpeusl69znynx/74892_levenhuk-rainbow-50l-amethyst_15_500_0afebfe0243ade7599a6c6c1063f3966.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: батарейный отсек',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 16'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh.jpg',
              'preview': '/upload/iv_resize_cache/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh_500_c6fbc40acb25434688a05de074ef59dd.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: сетевой адаптер',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 17'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9.jpg',
              'preview': '/upload/iv_resize_cache/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9_500_73d65e65c81edbecd0739e8244a01261.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: прочный пластиковый кейс',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 18'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx.jpg',
              'preview': '/upload/iv_resize_cache/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx_500_4848fa52bef0860df0e0812a29d6edfa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Amethyst: красивая подарочная коробка',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 19'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs.jpg',
              'preview': '/upload/iv_resize_cache/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs_500_987aef196b3dd5cafb328e85c68a51c1.webp',
              'description': 'Высушенный апельсин под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 20'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi.jpg',
              'preview': '/upload/iv_resize_cache/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi_500_82cfd171f0d8ba9fefe41b4875afcdbc.webp',
              'description': 'Пыль под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 21'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv.jpg',
              'preview': '/upload/iv_resize_cache/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv_500_70962e2df1877bb21d99d0c3cf61a590.webp',
              'description': 'Гитарные струны под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 22'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz.jpg',
              'preview': '/upload/iv_resize_cache/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz_500_cf83db71b24fd0d71c24e3d95bb6c54e.webp',
              'description': 'Можжевельник под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 23'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db.jpg',
              'preview': '/upload/iv_resize_cache/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db_500_89a413e0e9a594d6fe8ce966a9a717c8.webp',
              'description': 'Печать на ткани под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 24'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m.jpg',
              'preview': '/upload/iv_resize_cache/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m_500_8bf34baea4b43d14426c45aaf92cb024.webp',
              'description': 'Редис под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 25'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk.jpg',
              'preview': '/upload/iv_resize_cache/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk_500_ccc664529b3f604536505aa7c512b62b.webp',
              'description': 'Ножка паука под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 26'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v.jpg',
              'preview': '/upload/iv_resize_cache/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v_500_ae01fd454117998a5478475a5adf0952.webp',
              'description': 'Сера на спичке под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 27'
            }
          ],
          'shortSpecifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69052,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/info/brands/levenhuk/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            }
          ],
          'specifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69052,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/catalogue/mikroskopy/filter/override_reference_parameter_1022-is-levenhuk/apply/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип',
              'values': [
                {
                  'text': 'биологические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7492/apply/'
                },
                {
                  'text': 'световые/оптические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7499/apply/'
                },
              ]
            },
            {
              'name': 'Тип насадки',
              'values': [
                {
                  'text': 'монокулярная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1001-is-reference_parameteroption_7550/apply/'
                }
              ]
            },
            {
              'name': 'Материал оптики',
              'values': [
                {
                  'text': 'оптическое стекло',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Насадка',
              'values': [
                {
                  'text': 'поворотная на 360°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Угол наклона окулярной насадки',
              'values': [
                {
                  'text': '45°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Увеличение, крат',
              'values': [
                {
                  'text': '64 — 1280',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диаметр окулярной трубки, мм',
              'values': [
                {
                  'text': '23.2',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Окуляры',
              'values': [
                {
                  'text': 'WF16x',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Объективы',
              'values': [
                {
                  'text': 'ахроматические: 4x, 10x, 40xs (подпружиненный)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Револьверное устройство',
              'values': [
                {
                  'text': 'на 3 объектива',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Предметный столик, мм',
              'values': [
                {
                  'text': '90x90',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диапазон перемещения предметного столика, мм',
              'values': [
                {
                  'text': '0–11 по вертикали',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Характеристики предметного столика',
              'values': [
                {
                  'text': 'с зажимами',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Конденсор',
              'values': [
                {
                  'text': 'N.A. 0,65',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диафрагма',
              'values': [
                {
                  'text': 'диск с диафрагмами (6 отверстий)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Фокусировка',
              'values': [
                {
                  'text': 'грубая',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Корпус',
              'values': [
                {
                  'text': 'металл',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Подсветка',
              'values': [
                {
                  'text': 'светодиодная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_994-is-reference_parameteroption_7092/apply/'
                }
              ]
            },
            {
              'name': 'Регулировка яркости',
              'values': [
                {
                  'text': '',
                  'link': '',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Источник питания',
              'values': [
                {
                  'text': '2 батарейки типа AA (нет в комплекте), 220 В, 50 Гц',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Питание от батареек',
              'values': [
                {
                  'text': 'есть',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип лампы подсветки',
              'values': [
                {
                  'text': '3–3,2 В (нижний и верхний осветители)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень пользователя',
              'values': [
                {
                  'text': 'для начинающих',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень сложности сборки и настройки',
              'values': [
                {
                  'text': 'просто',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Назначение',
              'values': [
                {
                  'text': 'школьные/учебные',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1084-is-reference_parameteroption_9969/apply/'
                }
              ]
            },
            {
              'name': 'Расположение подсветки',
              'values': [
                {
                  'text': 'комбинированная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_875-is-reference_parameteroption_7392/apply/'
                }
              ]
            },
            {
              'name': 'Метод исследования',
              'values': [
                {
                  'text': 'светлое поле',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1000-is-reference_parameteroption_6888/apply/'
                }
              ]
            },
            {
              'name': 'Набор для опытов в комплекте',
              'values': [
                {
                  'text': '',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_876-from-1-to-1/apply/',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Линза Барлоу в комплекте',
              'values': [
                {
                  'text': false,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Чехол/кейс/сумка в комплекте',
              'values': [
                {
                  'text': 'чехол пылезащитный, кейс',
                  'link': ''
                }
              ]
            }
          ],
          'stocks': [
            {
              'name': 'Общий сток',
              'value': 'В наличии (14)',
              'tooltip': '',
              'status': 'IN_STOCK'
            }
          ],
          'availabilityCount': 0,
          'availability': [],
          'sale': null,
          'sales': [],
          'gifts': [
            {
              'src': 'http://placehold.it/100x100?text=lazy',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
          ]
        },
        {
          'id': 16325,
          'name': 'Микроскоп Levenhuk Rainbow 50L PLUS',
          'article': '69053',
          'url': '/catalogue/mikroskopy/microscope-levenhuk-rainbow-50l-plus-orange/?oid=16325',
          'margin': [],
          'currency': 'RUB',
          'priceValue': 15490,
          'price': '15 490 &#8381;',
          'oldPrice': '1235490 &#8381',
          'quantity': 25,
          'color': '#0099cc',
          'lang': null,
          'priceOnRequest': true,
          'share': [
            {
              'url': '#',
              'icon': 'copy-link',
              'text': 'Скопировать ссылку',
              'target': 'copy',
            },
            {
              'url': '#',
              'icon': 'vk',
              'text': 'Вконтакте',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'tg',
              'text': 'Telegram',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'whatsapp',
              'text': 'WhatsApp',
              'target': '_blank',
            },
          ],
          'status': {
            'code': 'IN_STOCK',
            'text': 'В наличии'
          },
          'badges': [
            {
              'icon': 'polygon',
              'code': 'free_shipping',
              'src': './images/badges/free-delivery.svg',
              'text': 'бесплатная<br>доставка',
							'title': 'Беслпатная доставка'
            },
            {
              'icon': 'polygon',
              'code': 'black_friday',
              'src': './images/badges/fire.svg',
              'text': 'черная<br>пятница',
							'title': 'Чёрная пятница'
            },
            {
              'icon': 'polygon',
              'code': 'gift',
              'text': 'подарок'
            },
            {
              'src': './images/badges/11-11.svg',
              'code': 'sale_11_11',
              'text': 'Роняем<br>цены',
							'title': 'Роняем цены'
            },
            {
              'icon': 'polygon',
              'code': 'hot',
              'text': 'бестселлер'
            },
            {
              'icon': 'infinity',
              'code': 'new',
              'text': 'новинки'
            },
            {
              'icon': 'infinity',
              'code': 'hot',
              'text': 'топ продаж'
            },
            {
              'icon': 'infinity',
              'code': 'sale',
              'text': 'распродажа'
            },
          ],
          'photos': [
            {
              'src': '/upload/iblock/2f2/armvtl8hkvlohjgfv3z5n24cnpj89j4a/69053_levenhuk-rainbow-50l-plus-azure_00.jpg',
              'preview': '/upload/iv_resize_cache/iblock/2f2/armvtl8hkvlohjgfv3z5n24cnpj89j4a/69053_levenhuk-rainbow-50l-plus-azure_00_500_3000d4ddff3e9f5f643e42cb0d3a51adcb40.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21.jpg',
              'preview': '/upload/iv_resize_cache/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21_500_503148476883d4df5451f62283e6556d.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: яркие биологические микроскопы с увеличением до 1280х. В комплекте набор для экспериментов, линза Барлоу, кейс. В линейке есть цифровая модель с камерой 2 Мпикс (в цвете Moonstone)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 2'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo.jpg',
              'preview': '/upload/iv_resize_cache/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo_500_40c086c959799f77e649417cf61c22e8.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: красивые учебные микроскопы для дома и школы',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 3'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/e9d/mwbzk1szxik30szovf2f2y2i6qclinux.jpg',
              'preview': '/upload/iv_resize_cache/uf/e9d/mwbzk1szxik30szovf2f2y2i6qclinux_500_9095efc7f8ddd5997e4002a5efc461e7.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: микроскоп и все аксессуары удобно и безопасно умещаются в кейсе',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 4'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f23/8s1aqjj5hamlif8htajcosrgxuy7g5s8/69053_levenhuk-rainbow-50l-plus-azure_04_ru.jpg',
              'preview': '/upload/iv_resize_cache/uf/f23/8s1aqjj5hamlif8htajcosrgxuy7g5s8/69053_levenhuk-rainbow-50l-plus-azure_04_ru_500_887e2283384244d61f7f276794456bf3.webp',
              'description': 'В комплекте с каждым микроскопом Levenhuk Rainbow набор для опытов: готовые микропрепараты и чистые предметные стекла, флаконы с образцами материалов, пинцет, микротом, инкубатор для артемии, пипетка, а также руководство «Интересный микроскоп. Изучаем микромир» ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 5'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f83/qvr5w0v2mmfz2wul2y6qtmrk7ev1evzu.jpg',
              'preview': '/upload/iv_resize_cache/uf/f83/qvr5w0v2mmfz2wul2y6qtmrk7ev1evzu_500_faddc7054b5444bcfa5776bd5d1f46fa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: монокулярный биологический микроскоп с увеличением от 64 до 1280 крат',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 6'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/05e/jakqyivyw0137ie90cc23no5d9ej1g1y.jpg',
              'preview': '/upload/iv_resize_cache/uf/05e/jakqyivyw0137ie90cc23no5d9ej1g1y_500_844d69237ed3fe33f574215e4686584e.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: окулярная насадка микроскопа наклонена на угол 45° к вертикали, может вращаться на 360° и фиксироваться в любом положении',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 7'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/e70/xbnmu13p15f1o2s7lird8eg8omp27d7j.jpg',
              'preview': '/upload/iv_resize_cache/uf/e70/xbnmu13p15f1o2s7lird8eg8omp27d7j_500_630eafddb6c782da423721d1e5a2b8a7.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: прочный металлический корпус идеален для частого использования в учебных заведениях или дома',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 8'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/fd2/e3ci6lekhp937t2xugmk94k1iumajy79.jpg',
              'preview': '/upload/iv_resize_cache/uf/fd2/e3ci6lekhp937t2xugmk94k1iumajy79_500_c4c5045501a4423aa0f893fd30f7b230.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: питание осветителей может производиться как от батареек, так и от сетевого адаптера',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 9'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/978/ba7uohmv40q36r5oaufoe7vtez12k921.jpg',
              'preview': '/upload/iv_resize_cache/uf/978/ba7uohmv40q36r5oaufoe7vtez12k921_500_575aa9e9e36cdd5365f1017450b6ad3a.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: револьверное устройство на 3 объектива: 4x, 10x, 40xs (подпружиненный)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 10'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/05e/d0i02n2dzkn0edgkgcwj6s2hn9rm1hln.jpg',
              'preview': '/upload/iv_resize_cache/uf/05e/d0i02n2dzkn0edgkgcwj6s2hn9rm1hln_500_a471a74f5105093eacac25e937188e2a.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: окуляр, установленный на микроскоп; качественная стеклянная оптика обеспечивает четкое изображение',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 11'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/cb8/lff5sdum6l7ftz0kz7gr0ufo9k0oea9l.jpg',
              'preview': '/upload/iv_resize_cache/uf/cb8/lff5sdum6l7ftz0kz7gr0ufo9k0oea9l_500_5057671baadb58b07499e43c3d949e07.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: перед окуляром можно установить линзу Барлоу, которая увеличит изображение вдвое',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 12'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky.jpg',
              'preview': '/upload/iv_resize_cache/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky_500_53369f00105838a76808acba08c062c9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: линза Барлоу 2x в комплекте',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 13'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/93d/w4g2xu1gp2e7f28fkoxtvlwaoi8tz5cc.jpg',
              'preview': '/upload/iv_resize_cache/uf/93d/w4g2xu1gp2e7f28fkoxtvlwaoi8tz5cc_500_5af9e2f60cfc6aab0905115fbda014de.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: верхняя и нижняя экономичные светодиодные подсветки с регулировкой яркости',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 14'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f7a/o5ue82ubzsvw8u7t64ligbuptuu766j4.jpg',
              'preview': '/upload/iv_resize_cache/uf/f7a/o5ue82ubzsvw8u7t64ligbuptuu766j4_500_b78274b314706f40bc476e3e4387b6aa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: снабжен удобными ручками фокусировки для перемещения предметного столика с зажимами',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 15'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/38f/q0ea57dlo8gv379a0pd6kjn15vx1ebbh.jpg',
              'preview': '/upload/iv_resize_cache/uf/38f/q0ea57dlo8gv379a0pd6kjn15vx1ebbh_500_f089c2afc38a4dd97dc847cd3ff39a89.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: батарейный отсек',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 16'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh.jpg',
              'preview': '/upload/iv_resize_cache/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh_500_c6fbc40acb25434688a05de074ef59dd.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: сетевой адаптер',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 17'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9.jpg',
              'preview': '/upload/iv_resize_cache/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9_500_73d65e65c81edbecd0739e8244a01261.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: прочный пластиковый кейс',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 18'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx.jpg',
              'preview': '/upload/iv_resize_cache/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx_500_4848fa52bef0860df0e0812a29d6edfa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Azure: красивая подарочная коробка',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 19'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs.jpg',
              'preview': '/upload/iv_resize_cache/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs_500_987aef196b3dd5cafb328e85c68a51c1.webp',
              'description': 'Высушенный апельсин под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 20'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi.jpg',
              'preview': '/upload/iv_resize_cache/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi_500_82cfd171f0d8ba9fefe41b4875afcdbc.webp',
              'description': 'Пыль под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 21'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv.jpg',
              'preview': '/upload/iv_resize_cache/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv_500_70962e2df1877bb21d99d0c3cf61a590.webp',
              'description': 'Гитарные струны под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 22'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz.jpg',
              'preview': '/upload/iv_resize_cache/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz_500_cf83db71b24fd0d71c24e3d95bb6c54e.webp',
              'description': 'Можжевельник под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 23'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db.jpg',
              'preview': '/upload/iv_resize_cache/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db_500_89a413e0e9a594d6fe8ce966a9a717c8.webp',
              'description': 'Печать на ткани под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 24'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m.jpg',
              'preview': '/upload/iv_resize_cache/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m_500_8bf34baea4b43d14426c45aaf92cb024.webp',
              'description': 'Редис под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 25'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk.jpg',
              'preview': '/upload/iv_resize_cache/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk_500_ccc664529b3f604536505aa7c512b62b.webp',
              'description': 'Ножка паука под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 26'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v.jpg',
              'preview': '/upload/iv_resize_cache/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v_500_ae01fd454117998a5478475a5adf0952.webp',
              'description': 'Сера на спичке под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 27'
            }
          ],
          'shortSpecifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69053,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/info/brands/levenhuk/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            }
          ],
          'specifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69053,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/catalogue/mikroskopy/filter/override_reference_parameter_1022-is-levenhuk/apply/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип',
              'values': [
                {
                  'text': 'биологические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7492/apply/'
                },
                {
                  'text': 'световые/оптические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7499/apply/'
                },
              ]
            },
            {
              'name': 'Тип насадки',
              'values': [
                {
                  'text': 'монокулярная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1001-is-reference_parameteroption_7550/apply/'
                }
              ]
            },
            {
              'name': 'Материал оптики',
              'values': [
                {
                  'text': 'оптическое стекло',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Насадка',
              'values': [
                {
                  'text': 'поворотная на 360°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Угол наклона окулярной насадки',
              'values': [
                {
                  'text': '45°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Увеличение, крат',
              'values': [
                {
                  'text': '64 — 1280',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диаметр окулярной трубки, мм',
              'values': [
                {
                  'text': '23.2',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Окуляры',
              'values': [
                {
                  'text': 'WF16x',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Объективы',
              'values': [
                {
                  'text': 'ахроматические: 4x, 10x, 40xs (подпружиненный)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Револьверное устройство',
              'values': [
                {
                  'text': 'на 3 объектива',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Предметный столик, мм',
              'values': [
                {
                  'text': '90x90',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диапазон перемещения предметного столика, мм',
              'values': [
                {
                  'text': '0–11 по вертикали',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Характеристики предметного столика',
              'values': [
                {
                  'text': 'с зажимами',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Конденсор',
              'values': [
                {
                  'text': 'N.A. 0,65',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диафрагма',
              'values': [
                {
                  'text': 'диск с диафрагмами (6 отверстий)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Фокусировка',
              'values': [
                {
                  'text': 'грубая',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Корпус',
              'values': [
                {
                  'text': 'металл',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Подсветка',
              'values': [
                {
                  'text': 'светодиодная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_994-is-reference_parameteroption_7092/apply/'
                }
              ]
            },
            {
              'name': 'Регулировка яркости',
              'values': [
                {
                  'text': '',
                  'link': '',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Источник питания',
              'values': [
                {
                  'text': '2 батарейки типа AA (нет в комплекте), 220 В, 50 Гц',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Питание от батареек',
              'values': [
                {
                  'text': 'есть',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип лампы подсветки',
              'values': [
                {
                  'text': '3–3,2 В (нижний и верхний осветители)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень пользователя',
              'values': [
                {
                  'text': 'для начинающих',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень сложности сборки и настройки',
              'values': [
                {
                  'text': 'просто',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Назначение',
              'values': [
                {
                  'text': 'школьные/учебные',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1084-is-reference_parameteroption_9969/apply/'
                }
              ]
            },
            {
              'name': 'Расположение подсветки',
              'values': [
                {
                  'text': 'комбинированная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_875-is-reference_parameteroption_7392/apply/'
                }
              ]
            },
            {
              'name': 'Метод исследования',
              'values': [
                {
                  'text': 'светлое поле',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1000-is-reference_parameteroption_6888/apply/'
                }
              ]
            },
            {
              'name': 'Набор для опытов в комплекте',
              'values': [
                {
                  'text': '',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_876-from-1-to-1/apply/',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Линза Барлоу в комплекте',
              'values': [
                {
                  'text': '2х',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Чехол/кейс/сумка в комплекте',
              'values': [
                {
                  'text': 'чехол пылезащитный, кейс',
                  'link': ''
                }
              ]
            }
          ],
          'stocks': [
            {
              'name': 'Общий сток',
              'value': 'В наличии (20+)',
              'tooltip': '',
              'status': 'IN_STOCK'
            }
          ],
          'availabilityCount': 0,
          'availability': [],
          'sale': null,
          'sales': [],
          'gifts': [
            {
              'src': 'http://placehold.it/100x100?text=lazy',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
          ]
        },
        {
          'id': 16328,
          'name': 'Микроскоп Levenhuk Rainbow 50L PLUS',
          'article': '69054',
          'url': '/catalogue/mikroskopy/microscope-levenhuk-rainbow-50l-plus-orange/?oid=16328',
          'margin': [],
          'currency': 'RUB',
          'priceValue': 15490,
          'price': '15 490 &#8381;',
          'oldPrice': '115490 &#8381',
          'quantity': 24,
          'color': '#ddf642',
          'lang': null,
          'share': [
            {
              'url': '#',
              'icon': 'copy-link',
              'text': 'Скопировать ссылку',
              'target': 'copy',
            },
            {
              'url': '#',
              'icon': 'vk',
              'text': 'Вконтакте',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'tg',
              'text': 'Telegram',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'whatsapp',
              'text': 'WhatsApp',
              'target': '_blank',
            },
          ],
          'status': {
            'code': 'IN_STOCK',
            'text': 'В наличии'
          },
          'badges': [
            {
              'icon': 'polygon',
              'code': 'guarantees',
              'text': '5 лет гарантийный срок'
            },
            {
              'icon': 'polygon',
              'code': 'duhrer',
              'link': 'https://www.google.com/',
              'title': 'Используется технология DűhrerSN',
            },
            {
              'icon': 'polygon',
              'code': 'schmieroll',
              'link': 'https://www.google.com/',
            },
            {
              'icon': 'polygon',
              'code': 'free_shipping',
              'src': './images/badges/free-delivery.svg',
              'text': 'бесплатная<br>доставка',
							'title': 'Беслпатная доставка'
            },
            {
              'icon': 'polygon',
              'code': 'cyber_monday',
              'src': './images/badges/cyber-monday.svg',
              'text': 'кибер<br>понедельник',
							'title': 'Кибер понедельник'
            },
            {
              'icon': 'polygon',
              'code': 'hot',
              'text': 'бестселлер'
            },
            {
              'icon': 'infinity',
              'code': 'lifetime',
              'text': 'пожизненная гарантия'
            },
            {
              'icon': 'infinity',
              'code': 'new',
              'text': 'новинки'
            },
            {
              'icon': 'infinity',
              'code': 'hot',
              'text': 'топ продаж'
            },
            {
              'icon': 'infinity',
              'code': 'sale',
              'text': 'распродажа'
            },
          ],
          'photos': [
            {
              'src': '/upload/iblock/ee2/eivgdvnig2jv5s153o0s6md2law310pc/69054_levenhuk-rainbow-50l-plus-lime_00.jpg',
              'preview': '/upload/iv_resize_cache/iblock/ee2/eivgdvnig2jv5s153o0s6md2law310pc/69054_levenhuk-rainbow-50l-plus-lime_00_500_3000fb15188caf995490fc46183449227550.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21.jpg',
              'preview': '/upload/iv_resize_cache/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21_500_503148476883d4df5451f62283e6556d.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: яркие биологические микроскопы с увеличением до 1280х. В комплекте набор для экспериментов, линза Барлоу, кейс. В линейке есть цифровая модель с камерой 2 Мпикс (в цвете Moonstone)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 2'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo.jpg',
              'preview': '/upload/iv_resize_cache/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo_500_40c086c959799f77e649417cf61c22e8.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: красивые учебные микроскопы для дома и школы',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 3'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/bba/vdj4sypbkj5rcufbo3ghyl8vg4gfegzp.jpg',
              'preview': '/upload/iv_resize_cache/uf/bba/vdj4sypbkj5rcufbo3ghyl8vg4gfegzp_500_9bb417f2844cb9ef2837a2d6f2e52922.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: микроскоп и все аксессуары удобно и безопасно умещаются в кейсе',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 4'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/e82/q142sxvmtvfi2y2uhyj48vwh199hedjz/69054_levenhuk-rainbow-50l-plus-lime_04_ru.jpg',
              'preview': '/upload/iv_resize_cache/uf/e82/q142sxvmtvfi2y2uhyj48vwh199hedjz/69054_levenhuk-rainbow-50l-plus-lime_04_ru_500_4b6f143dca48b1c319304c5b6d364e81.webp',
              'description': 'В комплекте с каждым микроскопом Levenhuk Rainbow набор для опытов: готовые микропрепараты и чистые предметные стекла, флаконы с образцами материалов, пинцет, микротом, инкубатор для артемии, пипетка, а также руководство «Интересный микроскоп. Изучаем микромир» ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 5'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/1f2/r8om27hd1xia4a6p7oakezzhrpqdezpv.jpg',
              'preview': '/upload/iv_resize_cache/uf/1f2/r8om27hd1xia4a6p7oakezzhrpqdezpv_500_0b0d7fb22076aa955356a268cddc115a.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: монокулярный биологический микроскоп с увеличением от 64 до 1280 крат',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 6'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c8a/5m6ccbktjjvn31qpjcudm0aao5r6dtqi.jpg',
              'preview': '/upload/iv_resize_cache/uf/c8a/5m6ccbktjjvn31qpjcudm0aao5r6dtqi_500_e6db77fde8b14e395b4355dc3f459f50.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: окулярная насадка микроскопа наклонена на угол 45° к вертикали, может вращаться на 360° и фиксироваться в любом положении',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 7'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/d6e/9s8fjfzsaxho79ykxaqigbwygfuksti1.jpg',
              'preview': '/upload/iv_resize_cache/uf/d6e/9s8fjfzsaxho79ykxaqigbwygfuksti1_500_7071fc17de6ffecc1fa4345fe69dfcc8.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: прочный металлический корпус идеален для частого использования в учебных заведениях или дома',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 8'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/8b8/suu4l5fm5ei9drkgbeq1uegritrmh0wc.jpg',
              'preview': '/upload/iv_resize_cache/uf/8b8/suu4l5fm5ei9drkgbeq1uegritrmh0wc_500_dc2c4bfbaacfbccb1d862fda9d1bf892.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: питание осветителей может производиться как от батареек, так и от сетевого адаптера',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 9'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/96c/3tv4jnww36wgin71hruehenmvg4se251.jpg',
              'preview': '/upload/iv_resize_cache/uf/96c/3tv4jnww36wgin71hruehenmvg4se251_500_098699e74600831377662ae3517b8e9d.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: револьверное устройство на 3 объектива: 4x, 10x, 40xs (подпружиненный)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 10'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/662/kqezxs7wy25v2hgod0kivkalg7skahgw.jpg',
              'preview': '/upload/iv_resize_cache/uf/662/kqezxs7wy25v2hgod0kivkalg7skahgw_500_84bb5731ac238c514b8408ffa3f6bcae.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: окуляр, установленный на микроскоп; качественная стеклянная оптика обеспечивает четкое изображение',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 11'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/0ee/iw37wdjze8ssjg4vpzzqkc0z9b9w48t2.jpg',
              'preview': '/upload/iv_resize_cache/uf/0ee/iw37wdjze8ssjg4vpzzqkc0z9b9w48t2_500_450517697cdc42ac83134cbdf3996528.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: перед окуляром можно установить линзу Барлоу, которая увеличит изображение вдвое',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 12'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky.jpg',
              'preview': '/upload/iv_resize_cache/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky_500_53369f00105838a76808acba08c062c9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: линза Барлоу 2x в комплекте',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 13'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/13f/ajwyuwqim0lnykr2qij49br5x3evq36b.jpg',
              'preview': '/upload/iv_resize_cache/uf/13f/ajwyuwqim0lnykr2qij49br5x3evq36b_500_42bab0bae65a0fd62d21a0762ff71069.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: верхняя и нижняя экономичные светодиодные подсветки с регулировкой яркости',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 14'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/a10/h2mrc7nhvip2aus3desfn0uppjbq4a0c.jpg',
              'preview': '/upload/iv_resize_cache/uf/a10/h2mrc7nhvip2aus3desfn0uppjbq4a0c_500_13b49f6451f30cdb61cb814ac7b2b9ba.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: снабжен удобными ручками фокусировки для перемещения предметного столика с зажимами',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 15'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/b30/ro6jiqfiqlpe5wnmcrabcyn0usp3ht6r.jpg',
              'preview': '/upload/iv_resize_cache/uf/b30/ro6jiqfiqlpe5wnmcrabcyn0usp3ht6r_500_7e5d99ea62c3d4dae0b6d31d43eb979c.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: батарейный отсек',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 16'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh.jpg',
              'preview': '/upload/iv_resize_cache/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh_500_c6fbc40acb25434688a05de074ef59dd.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: сетевой адаптер',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 17'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9.jpg',
              'preview': '/upload/iv_resize_cache/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9_500_73d65e65c81edbecd0739e8244a01261.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: прочный пластиковый кейс',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 18'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx.jpg',
              'preview': '/upload/iv_resize_cache/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx_500_4848fa52bef0860df0e0812a29d6edfa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Lime: красивая подарочная коробка',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 19'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs.jpg',
              'preview': '/upload/iv_resize_cache/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs_500_987aef196b3dd5cafb328e85c68a51c1.webp',
              'description': 'Высушенный апельсин под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 20'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi.jpg',
              'preview': '/upload/iv_resize_cache/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi_500_82cfd171f0d8ba9fefe41b4875afcdbc.webp',
              'description': 'Пыль под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 21'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv.jpg',
              'preview': '/upload/iv_resize_cache/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv_500_70962e2df1877bb21d99d0c3cf61a590.webp',
              'description': 'Гитарные струны под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 22'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz.jpg',
              'preview': '/upload/iv_resize_cache/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz_500_cf83db71b24fd0d71c24e3d95bb6c54e.webp',
              'description': 'Можжевельник под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 23'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db.jpg',
              'preview': '/upload/iv_resize_cache/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db_500_89a413e0e9a594d6fe8ce966a9a717c8.webp',
              'description': 'Печать на ткани под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 24'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m.jpg',
              'preview': '/upload/iv_resize_cache/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m_500_8bf34baea4b43d14426c45aaf92cb024.webp',
              'description': 'Редис под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 25'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk.jpg',
              'preview': '/upload/iv_resize_cache/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk_500_ccc664529b3f604536505aa7c512b62b.webp',
              'description': 'Ножка паука под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 26'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v.jpg',
              'preview': '/upload/iv_resize_cache/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v_500_ae01fd454117998a5478475a5adf0952.webp',
              'description': 'Сера на спичке под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 27'
            }
          ],
          'shortSpecifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69054,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/info/brands/levenhuk/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            }
          ],
          'specifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69054,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/catalogue/mikroskopy/filter/override_reference_parameter_1022-is-levenhuk/apply/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип',
              'values': [
                {
                  'text': 'биологические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7492/apply/'
                }
              ]
            },
            {
              'name': 'Тип насадки',
              'values': [
                {
                  'text': 'монокулярная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1001-is-reference_parameteroption_7550/apply/'
                }
              ]
            },
            {
              'name': 'Материал оптики',
              'values': [
                {
                  'text': 'оптическое стекло',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Насадка',
              'values': [
                {
                  'text': 'поворотная на 360°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Угол наклона окулярной насадки',
              'values': [
                {
                  'text': '45°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Увеличение, крат',
              'values': [
                {
                  'text': '64 — 1280',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диаметр окулярной трубки, мм',
              'values': [
                {
                  'text': '23.2',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Окуляры',
              'values': [
                {
                  'text': 'WF16x',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Объективы',
              'values': [
                {
                  'text': 'ахроматические: 4x, 10x, 40xs (подпружиненный)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Револьверное устройство',
              'values': [
                {
                  'text': 'на 3 объектива',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Предметный столик, мм',
              'values': [
                {
                  'text': '90x90',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диапазон перемещения предметного столика, мм',
              'values': [
                {
                  'text': '0–11 по вертикали',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Характеристики предметного столика',
              'values': [
                {
                  'text': 'с зажимами',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Конденсор',
              'values': [
                {
                  'text': 'N.A. 0,65',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диафрагма',
              'values': [
                {
                  'text': 'диск с диафрагмами (6 отверстий)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Фокусировка',
              'values': [
                {
                  'text': 'грубая',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Корпус',
              'values': [
                {
                  'text': 'металл',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Подсветка',
              'values': [
                {
                  'text': 'светодиодная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_994-is-reference_parameteroption_7092/apply/'
                }
              ]
            },
            {
              'name': 'Регулировка яркости',
              'values': [
                {
                  'text': '',
                  'link': '',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Источник питания',
              'values': [
                {
                  'text': '2 батарейки типа AA (нет в комплекте), 220 В, 50 Гц',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Питание от батареек',
              'values': [
                {
                  'text': 'есть',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип лампы подсветки',
              'values': [
                {
                  'text': '3–3,2 В (нижний и верхний осветители)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень пользователя',
              'values': [
                {
                  'text': 'для начинающих',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень сложности сборки и настройки',
              'values': [
                {
                  'text': 'просто',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Назначение',
              'values': [
                {
                  'text': 'школьные/учебные',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1084-is-reference_parameteroption_9969/apply/'
                }
              ]
            },
            {
              'name': 'Расположение подсветки',
              'values': [
                {
                  'text': 'комбинированная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_875-is-reference_parameteroption_7392/apply/'
                }
              ]
            },
            {
              'name': 'Метод исследования',
              'values': [
                {
                  'text': 'светлое поле',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1000-is-reference_parameteroption_6888/apply/'
                }
              ]
            },
            {
              'name': 'Набор для опытов в комплекте',
              'values': [
                {
                  'text': '',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_876-from-1-to-1/apply/',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Линза Барлоу в комплекте',
              'values': [
                {
                  'text': '2х',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Чехол/кейс/сумка в комплекте',
              'values': [
                {
                  'text': 'чехол пылезащитный, кейс',
                  'link': ''
                }
              ]
            }
          ],
          'stocks': [
            {
              'name': 'Общий сток',
              'value': 'В наличии (20+)',
              'tooltip': '',
              'status': 'IN_STOCK'
            }
          ],
          'availabilityCount': 10,
          'availability': [
            {
              'country': 'Россия',
              'icon': 'https://loc.levenhuk.com/upload/uf/876/0un55i10jr1x3o4ylv1c0bk01vxearqm/ru.png',
              'resellers': [
                {
                  'coordinates': [
                    '55.77592495510984',
                    '37.68445894032853'
                  ],
                  'resellerId': '2351',
                  'reseller': 'Магазин-флагман «Четыре глаза» у метро Бауманская',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Бакунинская ул., д. 23–41',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.76037332343009',
                    '37.62545196978284'
                  ],
                  'resellerId': '2352',
                  'reseller': 'Магазин «Четыре глаза» на Лубянке',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Театральный проезд, д. 5/1, «ЦДМ на Лубянке», 1 этаж, «Улица сказок»',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.66355',
                    '37.605539'
                  ],
                  'resellerId': '2353',
                  'reseller': 'Магазин «Четыре глаза» на Нахимовском',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Нахимовский пр., д. 11, корп. 1',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.81174',
                    '37.6045'
                  ],
                  'resellerId': '2354',
                  'reseller': 'Магазин «Четыре глаза» у метро Бутырская',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Огородный проезд, д. 9, корп. 18',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.870251',
                    '37.469581'
                  ],
                  'resellerId': '2355',
                  'reseller': 'Магазин «Четыре глаза» у метро Беломорская',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'Смольная ул., д. 63Б, ТЦ «Экстрим», 1 этаж, павильон А-11',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.7648016',
                    '37.5580848'
                  ],
                  'resellerId': '2356',
                  'reseller': 'Магазин «Четыре глаза» на Пресне',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': '<p>Звенигородское ш., д. 4, ТЦ &laquo;Электроника на Пресне&raquo;, 1 этаж, павильон А-41, красная линия</p>',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '55.66517080802077',
                    '37.481573398418334'
                  ],
                  'resellerId': '2358',
                  'reseller': 'Магазин «Четыре глаза» у метро Юго-Западная',
                  'city': 'Москва',
                  'url': '',
                  'extra': {
                    'address': 'ул. Покрышкина, д. 4, ТЦ «Звездочка», первый этаж',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '60.057879',
                    '30.33205'
                  ],
                  'resellerId': '2374',
                  'reseller': 'Магазин «Четыре глаза» на Шостаковича',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Шостаковича ул., д. 5, корп. 1, (вход с улицы)',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '59.83207666720598',
                    '30.38698684028511'
                  ],
                  'resellerId': '2375',
                  'reseller': 'Магазин «Четыре глаза» на Гашека',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Ярослава Гашека ул., д. 4, корп. 1',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 21:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '60.012253',
                    '30.398492'
                  ],
                  'resellerId': '2377',
                  'reseller': 'Магазин «Четыре глаза» на Академической',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Гражданский пр., д. 41, корп. 2, лит. Б, ТК «Академ-Парк», цокольный этаж, павильон G12,2(3)\n',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '60.0029139',
                    '30.2289583'
                  ],
                  'resellerId': '2378',
                  'reseller': 'Магазин «Четыре глаза» на Богатырском',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Богатырский пр., д. 42, лит. А, гипермаркет «О’кей», помещение 49\n',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                },
                {
                  'coordinates': [
                    '59.88164800000001',
                    '30.315893'
                  ],
                  'resellerId': '2380',
                  'reseller': 'Магазин «Четыре глаза» на Электросиле',
                  'city': 'Санкт-Петербург (СПб)',
                  'url': '',
                  'extra': {
                    'address': 'Московский пр., д. 137, лит. А, гипермаркет «О’кей», помещение 24',
                    'phone': [ '+7 (999) 504-25-00'],
                    'email': '',
                    'zip_code': '',
                    'time': 'с 10:00 до 22:00 ежедневно, без перерывов'
                  },
                  'reserve': true
                }
              ]
            }
          ],
          'sale': null,
          hasSale: true,
          'sales': [],
          'gifts': [
            {
              'src': 'http://placehold.it/100x100?text=lazy',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
          ]
        },
        {
          'id': 16331,
          'name': 'Микроскоп Levenhuk Rainbow 50L PLUS',
          'article': '69055',
          'url': '/catalogue/mikroskopy/microscope-levenhuk-rainbow-50l-plus-orange/?oid=16331',
          'margin': [],
          'currency': 'RUB',
          'priceValue': 15490,
          'price': '15 490 &#8381;',
          'oldPrice': '',
          'quantity': 80,
          'color': '#e03c31',
          hasSale: true,
          'lang': null,
          'share': [
            {
              'url': '#',
              'icon': 'copy-link',
              'text': 'Скопировать ссылку',
              'target': 'copy',
            },
            {
              'url': '#',
              'icon': 'vk',
              'text': 'Вконтакте',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'tg',
              'text': 'Telegram',
              'target': '_blank',
            },
            {
              'url': '#',
              'icon': 'whatsapp',
              'text': 'WhatsApp',
              'target': '_blank',
            },
          ],
          'status': {
            'code': 'IN_STOCK',
            'text': 'В наличии'
          },
          'badges': [
            {
              'icon': 'polygon',
              'code': 'free_shipping',
              'src': './images/badges/free-delivery.svg',
              'text': 'бесплатная<br>доставка',
							'title': 'Беслпатная доставка'
            },
            {
              'icon': 'polygon',
              'code': 'gift',
              'text': 'подарок'
            },
						{
							'src': './images/badges/fire.svg',
							'code': 'black_friday',
							'text': 'Чёрная<br>пятница',
							'title': 'Чёрная пятница'
						},
            {
              'src': './images/badges/11-11.svg',
              'code': 'sale_11_11',
              'text': 'Роняем<br>цены',
							'title': 'Роняем цены'
            },
						{
							'src': './images/badges/cyber-monday.svg',
							'code': 'cyber_monday',
							'text': 'Кибер<br>понедельник',
							'title': 'Кибер понедельник'
						},
            {
              'src': './images/badges/cyber-summer.svg',
              'code': 'cyber_summer',
              'text': 'Кибер<br>лето',
              'title': 'Кибер лето'
            },
            {
              'icon': 'polygon',
              'code': 'hot',
              'text': 'бестселлер'
            },
            {
              'icon': 'infinity',
              'code': 'lifetime',
              'text': 'пожизненная гарантия'
            },
            {
              'icon': 'infinity',
              'code': 'hot',
              'text': 'топ продаж'
            },
          ],
          'photos': [
            {
              'src': '/upload/iblock/666/51osp40tc6flhtknpngmmel10bne245y/69055_levenhuk-rainbow-50l-plus-orange_00.jpg',
              'preview': '/upload/iv_resize_cache/iblock/666/51osp40tc6flhtknpngmmel10bne245y/69055_levenhuk-rainbow-50l-plus-orange_00_500_3000d1bb3a8c0971cf4dacfae10cf39f3307.webp',
              'description': '',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 1'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21.jpg',
              'preview': '/upload/iv_resize_cache/uf/ce3/3vtv18j2svtrhglf7if360jgmloe3f21_500_503148476883d4df5451f62283e6556d.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: яркие биологические микроскопы с увеличением до 1280х. В комплекте набор для экспериментов, линза Барлоу, кейс. В линейке есть цифровая модель с камерой 2 Мпикс (в цвете Moonstone)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 2'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo.jpg',
              'preview': '/upload/iv_resize_cache/uf/ef2/ov1dqmudhzwdf61wbs4jruqzrup5fqjo_500_40c086c959799f77e649417cf61c22e8.webp',
              'description': 'Levenhuk Rainbow 50L PLUS: красивые учебные микроскопы для дома и школы',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 3'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/705/1ebqcmeyhoih3014483l0wg1ynpi8o29.jpg',
              'preview': '/upload/iv_resize_cache/uf/705/1ebqcmeyhoih3014483l0wg1ynpi8o29_500_2492f792a29cac08ea51f090eaf05446.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: микроскоп и все аксессуары удобно и безопасно умещаются в кейсе',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 4'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/e6f/q5f23acwenmkf4nwsgbe0yzs1d84m042/69055_levenhuk-rainbow-50l-plus-orange_04_ru.jpg',
              'preview': '/upload/iv_resize_cache/uf/e6f/q5f23acwenmkf4nwsgbe0yzs1d84m042/69055_levenhuk-rainbow-50l-plus-orange_04_ru_500_91ab459d7724afdf50ee2bf4134c7916.webp',
              'description': 'В комплекте с каждым микроскопом Levenhuk Rainbow набор для опытов: готовые микропрепараты и чистые предметные стекла, флаконы с образцами материалов, пинцет, микротом, инкубатор для артемии, пипетка, а также руководство «Интересный микроскоп. Изучаем микромир» ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 5'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/655/l3xjt1j1ibynj4rfc4tfhg2eb5sobu2j.jpg',
              'preview': '/upload/iv_resize_cache/uf/655/l3xjt1j1ibynj4rfc4tfhg2eb5sobu2j_500_b184a58d38df3b4bdb21aad0783500ac.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: монокулярный биологический микроскоп с увеличением от 64 до 1280 крат',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 6'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ecf/fv7b3fhk4lcthw5iyk8z6qyh9dgrb003.jpg',
              'preview': '/upload/iv_resize_cache/uf/ecf/fv7b3fhk4lcthw5iyk8z6qyh9dgrb003_500_d6bcf20deea79990acbe7d7f72269ede.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: окулярная насадка микроскопа наклонена на угол 45° к вертикали, может вращаться на 360° и фиксироваться в любом положении',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 7'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c29/crpa8xk3kz0uah6ztzjvjslv7zd75w6p.jpg',
              'preview': '/upload/iv_resize_cache/uf/c29/crpa8xk3kz0uah6ztzjvjslv7zd75w6p_500_ab9dffbc617b2e7f244def572b516cfb.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: прочный металлический корпус идеален для частого использования в учебных заведениях или дома',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 8'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/7b1/p3yd0yhnx4qwc8llb64affemukpgwdef.jpg',
              'preview': '/upload/iv_resize_cache/uf/7b1/p3yd0yhnx4qwc8llb64affemukpgwdef_500_a04d5d738b012895413fbb9a03aa2d8d.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: питание осветителей может производиться как от батареек, так и от сетевого адаптера',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 9'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/aa7/xmf5a7jt9ha4bqtutkglpmozdsf7p75s.jpg',
              'preview': '/upload/iv_resize_cache/uf/aa7/xmf5a7jt9ha4bqtutkglpmozdsf7p75s_500_1ecc2d063d300b7f7326d4f1a7f2d631.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: револьверное устройство на 3 объектива: 4x, 10x, 40xs (подпружиненный)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 10'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/759/dmiokehzehmty5edsn7ki4phzui1nq9i.jpg',
              'preview': '/upload/iv_resize_cache/uf/759/dmiokehzehmty5edsn7ki4phzui1nq9i_500_82a868dff1872e9904aa978dc049ffd6.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: окуляр, установленный на микроскоп; качественная стеклянная оптика обеспечивает четкое изображение',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 11'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/2d5/nkpuxsucgqdwj8fhb0ky568e0486ga5u.jpg',
              'preview': '/upload/iv_resize_cache/uf/2d5/nkpuxsucgqdwj8fhb0ky568e0486ga5u_500_c6ae82e5e0ca1027def204bd9c7ef07f.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: перед окуляром можно установить линзу Барлоу, которая увеличит изображение вдвое',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 12'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky.jpg',
              'preview': '/upload/iv_resize_cache/uf/ea0/ynl6nmusg0hjs8m9ynjcc3n6oqz6paky_500_53369f00105838a76808acba08c062c9.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: линза Барлоу 2x в комплекте',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 13'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/ca1/k97pzk4drixzyh1o2454wf4sflvjgmp0.jpg',
              'preview': '/upload/iv_resize_cache/uf/ca1/k97pzk4drixzyh1o2454wf4sflvjgmp0_500_63c8df45bedd9d4cc754e825cd243fa8.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: верхняя и нижняя экономичные светодиодные подсветки с регулировкой яркости',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 14'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f77/rqwhg1ugwbyb4m5vvwjthw90sroshoeo.jpg',
              'preview': '/upload/iv_resize_cache/uf/f77/rqwhg1ugwbyb4m5vvwjthw90sroshoeo_500_3670b5429a9533e93c74396a85543128.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: снабжен удобными ручками фокусировки для перемещения предметного столика с зажимами',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 15'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/afd/wksecxqqslggpimnr3m6ssy5ntg9iism.jpg',
              'preview': '/upload/iv_resize_cache/uf/afd/wksecxqqslggpimnr3m6ssy5ntg9iism_500_0eb267930d4718ab555bc0e6a402b810.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: батарейный отсек',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 16'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh.jpg',
              'preview': '/upload/iv_resize_cache/uf/391/lsxbe5bg6xwnpyr3vqs3nsgm6krzqgoh_500_c6fbc40acb25434688a05de074ef59dd.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: сетевой адаптер',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 17'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9.jpg',
              'preview': '/upload/iv_resize_cache/uf/3f3/qdxc1qwgz4nanz1kt01vqcgptrh2qxb9_500_73d65e65c81edbecd0739e8244a01261.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: прочный пластиковый кейс',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 18'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx.jpg',
              'preview': '/upload/iv_resize_cache/uf/4fb/gaenxo8cj6ofne6u2efi8q9aekzjl0wx_500_4848fa52bef0860df0e0812a29d6edfa.webp',
              'description': 'Микроскоп Levenhuk Rainbow 50L PLUS Orange: красивая подарочная коробка',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 19'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs.jpg',
              'preview': '/upload/iv_resize_cache/uf/350/3ayfzqrnuy5ujnyox4a5d4skdxbo38cs_500_987aef196b3dd5cafb328e85c68a51c1.webp',
              'description': 'Высушенный апельсин под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 20'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi.jpg',
              'preview': '/upload/iv_resize_cache/uf/c92/nxkwa2blko8yxsk2iztjz9grog0mc2gi_500_82cfd171f0d8ba9fefe41b4875afcdbc.webp',
              'description': 'Пыль под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 21'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv.jpg',
              'preview': '/upload/iv_resize_cache/uf/031/0wi553c56rozcqv98dv5j21lhy6sg5uv_500_70962e2df1877bb21d99d0c3cf61a590.webp',
              'description': 'Гитарные струны под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 22'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz.jpg',
              'preview': '/upload/iv_resize_cache/uf/f28/q8m2tgcxc9yrmw82cmtxh40g4o6yi2jz_500_cf83db71b24fd0d71c24e3d95bb6c54e.webp',
              'description': 'Можжевельник под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 23'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db.jpg',
              'preview': '/upload/iv_resize_cache/uf/43e/cch5fkzyswxph7szifuiczgm6k3sw3db_500_89a413e0e9a594d6fe8ce966a9a717c8.webp',
              'description': 'Печать на ткани под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фото',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 24'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m.jpg',
              'preview': '/upload/iv_resize_cache/uf/4b7/xi89n76tt9uk8rwiaqlltee23lqmbk3m_500_8bf34baea4b43d14426c45aaf92cb024.webp',
              'description': 'Редис под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS картинка',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 25'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk.jpg',
              'preview': '/upload/iv_resize_cache/uf/9a5/4309qawx8ze2x525gkqfqzq9ia2d9zsk_500_ccc664529b3f604536505aa7c512b62b.webp',
              'description': 'Ножка паука под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo) ',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS изображение',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 26'
            },
            {
              'src': 'https://loc.levenhuk.com/upload/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v.jpg',
              'preview': '/upload/iv_resize_cache/uf/c05/4ekvwba3ctu4hyboxrcwtedwywztn40v_500_ae01fd454117998a5478475a5adf0952.webp',
              'description': 'Сера на спичке под микроскопом Levenhuk Rainbow 50L PLUS. Автор: Руслан Бикбаев (@bikart_photo)',
              'alt': 'Микроскоп Levenhuk Rainbow 50L PLUS фотография',
              'title': 'Микроскоп Levenhuk Rainbow 50L PLUS, изображение 27'
            }
          ],
          'shortSpecifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69055,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/info/brands/levenhuk/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            }
          ],
          'specifications': [
            {
              'name': 'Артикул',
              'values': [
                {
                  'text': 69055,
                  'link': ''
                }
              ]
            },
            {
              'name': 'Бренд',
              'values': [
                {
                  'text': 'Levenhuk (Левенгук)',
                  'link': '/catalogue/mikroskopy/filter/override_reference_parameter_1022-is-levenhuk/apply/'
                }
              ]
            },
            {
              'name': 'Гарантия',
              'values': [
                {
                  'text': 'бессрочная',
                  'link': ''
                }
              ]
            },
            {
              'name': 'EAN',
              'values': [
                {
                  'text': '0611901505909',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Размер упаковки (ДxШxВ)',
              'values': [
                {
                  'text': '18x40x27 см',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Вес упаковки',
              'values': [
                {
                  'text': '3.24 кг',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип',
              'values': [
                {
                  'text': 'биологические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7492/apply/'
                },
                {
                  'text': 'световые/оптические',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1005-is-reference_parameteroption_7499/apply/'
                },
              ]
            },
            {
              'name': 'Тип насадки',
              'values': [
                {
                  'text': 'монокулярная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1001-is-reference_parameteroption_7550/apply/'
                }
              ]
            },
            {
              'name': 'Материал оптики',
              'values': [
                {
                  'text': 'оптическое стекло',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Насадка',
              'values': [
                {
                  'text': 'поворотная на 360°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Угол наклона окулярной насадки',
              'values': [
                {
                  'text': '45°',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Увеличение, крат',
              'values': [
                {
                  'text': '64 — 1280',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диаметр окулярной трубки, мм',
              'values': [
                {
                  'text': '23.2',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Окуляры',
              'values': [
                {
                  'text': 'WF16x',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Объективы',
              'values': [
                {
                  'text': 'ахроматические: 4x, 10x, 40xs (подпружиненный)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Револьверное устройство',
              'values': [
                {
                  'text': 'на 3 объектива',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Предметный столик, мм',
              'values': [
                {
                  'text': '90x90',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диапазон перемещения предметного столика, мм',
              'values': [
                {
                  'text': '0–11 по вертикали',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Характеристики предметного столика',
              'values': [
                {
                  'text': 'с зажимами',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Конденсор',
              'values': [
                {
                  'text': 'N.A. 0,65',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Диафрагма',
              'values': [
                {
                  'text': 'диск с диафрагмами (6 отверстий)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Фокусировка',
              'values': [
                {
                  'text': 'грубая',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Корпус',
              'values': [
                {
                  'text': 'металл',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Подсветка',
              'values': [
                {
                  'text': 'светодиодная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_994-is-reference_parameteroption_7092/apply/'
                }
              ]
            },
            {
              'name': 'Регулировка яркости',
              'values': [
                {
                  'text': '',
                  'link': '',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Источник питания',
              'values': [
                {
                  'text': '2 батарейки типа AA (нет в комплекте), 220 В, 50 Гц',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Питание от батареек',
              'values': [
                {
                  'text': 'есть',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Тип лампы подсветки',
              'values': [
                {
                  'text': '3–3,2 В (нижний и верхний осветители)',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень пользователя',
              'values': [
                {
                  'text': 'для начинающих',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Уровень сложности сборки и настройки',
              'values': [
                {
                  'text': 'просто',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Назначение',
              'values': [
                {
                  'text': 'школьные/учебные',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1084-is-reference_parameteroption_9969/apply/'
                }
              ]
            },
            {
              'name': 'Расположение подсветки',
              'values': [
                {
                  'text': 'комбинированная',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_875-is-reference_parameteroption_7392/apply/'
                }
              ]
            },
            {
              'name': 'Метод исследования',
              'values': [
                {
                  'text': 'светлое поле',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_1000-is-reference_parameteroption_6888/apply/'
                }
              ]
            },
            {
              'name': 'Набор для опытов в комплекте',
              'values': [
                {
                  'text': '',
                  'link': '/catalogue/mikroskopy/filter/reference_parameter_876-from-1-to-1/apply/',
                  'type': 'boolean'
                }
              ]
            },
            {
              'name': 'Линза Барлоу в комплекте',
              'values': [
                {
                  'text': '2х',
                  'link': ''
                }
              ]
            },
            {
              'name': 'Чехол/кейс/сумка в комплекте',
              'values': [
                {
                  'text': 'чехол пылезащитный, кейс',
                  'link': ''
                }
              ]
            }
          ],
          'stocks': [
            {
              'name': 'Общий сток',
              'value': 'В наличии (20+)',
              'tooltip': '',
              'status': 'IN_STOCK'
            }
          ],
          'availabilityCount': 0,
          'availability': [],
          'sale': null,
          'sales': [],
          'gifts': [
            {
              'src': 'http://placehold.it/100x100?text=lazy',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание '
            },
            {
              'src': './images/default-image/default-image.svg',
              'href': '#',
              'title': 'Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание Какое-то описание '
            },
          ]
        }
      ],
    }
  };
  Array.from(document.querySelectorAll('.product-detail:not(.product-detail_inited)')).forEach((elem) => {
    new ProductDetail({ elem, options });
  });
}

// Экспорт;
window.Block.ProductDetail = ProductDetail;
